/* Bike single-product configurator ("Complete your bike").
   Pure helpers live on window.BkrmConfigurator so they are unit-testable
   in jsdom; the bike-configurator custom element (DOM/overlay) is added in
   later tasks. */
(function () {
  // parsePriceCents: resolve a config row's price to integer cents. The input
  // is `data-price ?? textContent`, which is one of two shapes — disambiguated
  // by STRUCTURE, never by magnitude:
  //   1. A bare integer (e.g. "4999") — a Shopify variant price, already in
  //      cents. Passed through unchanged. (This is what the liquid emits via
  //      data-price="{{ v.price }}", so it is the production path.)
  //   2. A money-formatted string (e.g. "€199,00", "€4.999,00", "2 780,00 kr")
  //      — the rendered textContent fallback, in whatever currency Shopify
  //      Markets served the page. Parsed as major units, then ×100.
  // The previous version guessed the shape from the value (< 1000 ⇒ units),
  // which mis-priced cheap items (data-price "500" → €500,00) and formatted
  // amounts ≥ €1000 ("€4.999,00" → €49,99). Structure removes the ambiguity.
  function parsePriceCents(value) {
    if (value == null) return 0;
    var str = String(value).trim();
    if (!str) return 0;
    // Shape 1: a raw integer string is already cents.
    if (/^\d+$/.test(str)) return parseInt(str, 10);
    // Shape 2: a formatted money string. Keep digits and the two separators
    // Shopify can use as a decimal point ("." and ","), dropping the currency
    // symbol, apostrophe grouping (CHF) and space grouping (SEK/NOK/PLN…).
    // Then trim separators the symbol itself contributed — "Dhs. 20,445.00"
    // strips to ".20,445.00", and a naive parseFloat would read 0.20445.
    var cleaned = str
      .replace(/[^\d.,]/g, "")
      .replace(/^[.,]+/, "")
      .replace(/[.,]+$/, "");
    if (!cleaned) return 0;
    // Which separator is the decimal point depends on the currency, and this
    // string carries no locale marker. VariantSelects.readDisplayedPriceCents
    // (assets/product.js) solves the same problem by reading the convention out
    // of Shopify.money_format; here we infer it from the string instead, because
    // this helper is a pure function that must also work before Shopify's
    // currency globals exist (see formatCents' own guard below).
    // Grouping is ALWAYS 3 digits, so a final
    // separator followed by exactly 2 digits can only be a decimal point:
    //   "1,299.99" / "1.134,65" / "1'134.65" -> decimal, 2 fraction digits
    //   "7,884,000" / "1.500" / "1,135"      -> grouping only, whole units
    // Hardcoding the Italian convention here (the old behaviour) turned
    // "$1,299.99" into €1,30 and "CHF 1'134.65" into CHF 11'346,50.
    var units;
    if (/[.,]\d{2}$/.test(cleaned)) {
      units = parseFloat(
        cleaned.slice(0, -3).replace(/[.,]/g, "") + "." + cleaned.slice(-2),
      );
    } else {
      units = parseFloat(cleaned.replace(/[.,]/g, ""));
    }
    if (!isFinite(units)) return 0;
    return Math.round(units * 100);
  }

  // formatCents: format integer cents as a euro string (Italian locale).
  // Always two decimals after the comma (e.g. "€4.999,00") — the summary
  // is a price recap, so amounts must read as currency.
  function formatCents(cents) {
    if (cents === 0) {
      // Etichetta tradotta dal Liquid (cfg_t_free): "Free" fisso restava in
      // inglese nel pannello del totale in tutte le lingue.
      var cfgRoot = document.querySelector("bike-configurator[data-t-free]");
      return (cfgRoot && cfgRoot.dataset.tFree) || "Free";
    }
    // Prefer Shopify's money formatter (shop-currency accurate). Fall back to a
    // local Italian format when it isn't available — e.g. before Shopify's
    // currency script loads, or in unit tests — so the recap never throws.
    if (window.Shopify && typeof window.Shopify.formatMoney === "function") {
      return window.Shopify.formatMoney(cents);
    }
    return (
      "€" +
      (cents / 100).toLocaleString("it-IT", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    );
  }

  // formatOfferGroup / parseOfferGroup: thousands-separator display for the
  // bid offer input (feedback Mattia — match the grouping already used by
  // og_price/slider labels in the same panel). Kept separate from formatCents:
  // that one is a full currency string with the shop's money format (symbol +
  // forced decimals), this is a bare number — the offer is whole units, and
  // the currency symbol already lives in the adjacent
  // .bid-offer-input-currency span.
  //
  // The separators are read back off the money format instead of being
  // hardcoded, because "same as the labels beside it" is not a fixed
  // convention: this shop presents EUR as "€1.000.000,00"
  // (amount_with_comma_separator) while a market on SEK renders
  // "1 000 000,00 kr" (amount_with_space_separator). A hardcoded en-US
  // grouping made one amount read two ways inside a single panel — "4,981" in
  // the field the user edits vs "€4.981,00" in the slider labels and the
  // recap right next to it.
  function offerNumberParts() {
    // One million with cents: the char between the leading 1 and the first
    // group of three is the grouping separator, the one before the final two
    // digits is the decimal mark. No match on the group => a format that
    // doesn't group at all, so don't invent grouping.
    var sample = formatCents(100000000);
    // formatMoney can come back empty for a money_format case it doesn't
    // handle (the theme ships two divergent money formatters). Fall back to
    // the local format so the separators — and therefore parseOfferGroup —
    // stay right even when the currency string itself fails to render.
    if (!/\d/.test(sample)) {
      sample = (1000000).toLocaleString("it-IT", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    }
    var group = /1(\D)0{3}/.exec(sample);
    var decimal = /(\D)\d{2}\D*$/.exec(sample);
    return {
      group: group ? group[1] : "",
      decimal: decimal ? decimal[1] : ",",
    };
  }

  function formatOfferGroup(value) {
    var n = Number(value);
    if (!isFinite(n)) return "";
    var parts = offerNumberParts();
    var abs = Math.abs(n);
    var whole = Math.floor(abs);
    var out = String(whole);
    if (parts.group) {
      out = out.replace(/\B(?=(\d{3})+(?!\d))/g, parts.group);
    }
    // Anchors are rounded to whole units, but priceMajor — the clamp ceiling
    // in syncBidOffer — can carry cents, so keep a fractional tail instead of
    // truncating the user's value out from under them.
    var cents = Math.round((abs - whole) * 100);
    if (cents) {
      out += parts.decimal + (cents < 10 ? "0" + cents : String(cents));
    }
    return (n < 0 ? "-" : "") + out;
  }

  function parseOfferGroup(str) {
    var s = String(str == null ? "" : str);
    var parts = offerNumberParts();
    // Drop the grouping separator before anything else. The old cleanup kept
    // digits, "." and "-", so on this shop's format it read a natural "4.981"
    // as 4.981 — a €4,98 offer instead of €4.981.
    if (parts.group) s = s.split(parts.group).join("");
    if (parts.decimal !== ".") s = s.split(parts.decimal).join(".");
    var cleaned = s.replace(/[^\d.-]/g, "");
    return cleaned === "" ? NaN : Number(cleaned);
  }

  // upsellScrollStep: distance to scroll the upsell carousel per arrow click.
  // Must match the CSS scroll-snap grid (card width + list gap). A hardcoded
  // 284px didn't line up with the actual 240px card + 12px gap grid, so
  // scroll-snap-type:mandatory (product-configurator.css) fought the
  // requested scrollBy animation — it stalled, then jerked to the nearest
  // valid snap point instead of landing there directly.
  function upsellScrollStep(listEl) {
    var card = listEl.querySelector(".card-drawer-upsell-item");
    if (!card) return listEl.clientWidth;
    var gap = parseFloat(getComputedStyle(listEl).columnGap) || 0;
    return card.offsetWidth + gap;
  }

  // upsellScrollToCard: move the carousel one card left/right, landing EXACTLY
  // on a card boundary. The previous version scrolled by a relative ±step from
  // wherever scrollLeft happened to be — any misalignment (a second click
  // interrupting the tween mid-flight, the clamp at the end of the strip, a
  // touch drag) then persisted forever, leaving cut-off cards on both edges
  // (screenshot review, 2026-07-04). Instead: round the current position to
  // the nearest card index, step the index, and tween to that absolute
  // offset, clamped to the scrollable range. Rapid clicks chain off the
  // pending target (not the mid-animation position) so each click advances
  // exactly one card.
  // The tween itself stays manual (rAF): Safari's support for smooth
  // programmatic scrolling has long been inconsistent — it can silently
  // ignore `behavior:"smooth"` and jump instantly (video review, 2026-07-02).
  function upsellScrollToCard(listEl, dir) {
    var step = upsellScrollStep(listEl);
    if (!step) return;
    var maxScroll = Math.max(0, listEl.scrollWidth - listEl.clientWidth);
    var base =
      listEl._cdScrollTarget != null ? listEl._cdScrollTarget : listEl.scrollLeft;
    var target = (Math.round(base / step) + dir) * step;
    target = Math.max(0, Math.min(target, maxScroll));
    listEl._cdScrollTarget = target;
    var duration = 260;
    var start = listEl.scrollLeft;
    var delta = target - start;
    var startTime = null;
    function easeOutCubic(t) {
      return 1 - Math.pow(1 - t, 3);
    }
    function frame(ts) {
      // A newer click retargeted the tween — let its own rAF loop take over.
      if (listEl._cdScrollTarget !== target) return;
      if (startTime === null) startTime = ts;
      var t = Math.min(1, (ts - startTime) / duration);
      listEl.scrollLeft = start + delta * easeOutCubic(t);
      if (t < 1) requestAnimationFrame(frame);
      else listEl._cdScrollTarget = null;
    }
    requestAnimationFrame(frame);
  }

  // escapeHtml: upsell titles come from the cart-add response and are injected
  // as innerHTML into the summary, so neutralize any markup-significant chars.
  function escapeHtml(s) {
    return String(s == null ? "" : s).replace(/[&<>"']/g, function (c) {
      return {
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
      }[c];
    });
  }

  // sizedImageUrl: mirrors the `image | img_url: 'WIDTHx0'` Liquid filter so
  // the JS-driven variant swap produces the same CDN URL shape the server
  // renders (insert "_{width}x" before the extension, before any query string).
  function sizedImageUrl(src, width) {
    var match = String(src).match(/^(.*)(\.[a-zA-Z0-9]+)(\?.*)?$/);
    if (!match) return src;
    return match[1] + "_" + width + "x" + match[2] + (match[3] || "");
  }

  // track: analytics BEST-EFFORT, mai sul percorso critico.
  // `window.posthog` non basta come guardia: lo snippet in theme.liquid
  // assegna `window.posthog = []` (array, quindi truthy) e attacca i
  // metodi-coda (capture, identify, …) solo DENTRO posthog.init(). Se init
  // non viene chiamato — il tema lo salta di proposito sotto automazione
  // (navigator.webdriver), e un'estensione privacy può lasciare lo stub a
  // metà — `window.posthog.capture` è undefined e la vecchia guardia
  // `if (window.posthog)` lasciava passare una chiamata che LANCIA.
  // Costava l'acquisto: in open() il capture è la prima istruzione (drawer
  // che non si apre), in _commitAndRedirect() sta fra il POST /cart/add.js
  // e il window.location.assign (bici in carrello, redirect mai partito,
  // errore generico a schermo). Asana 1217141601941185.
  function track(event, props) {
    try {
      if (window.posthog && typeof window.posthog.capture === "function") {
        window.posthog.capture(event, props);
      }
    } catch (err) {
      if (window.console && window.console.debug)
        window.console.debug("[bkrm] analytics skipped:", event, err);
    }
  }

  // Wizard a sette passi (handoff "Configuratore bici", call con Umberto
  // dell'08/10/2026): uno step per scelta invece del vecchio "Pedals &
  // accessories" che ammucchiava personalizzazione e tre caroselli. La box
  // e' scesa in fondo, subito prima del recap.
  //
  // Kit, box e review ci sono sempre. Gli altri esistono solo se hanno
  // contenuto per QUESTA bici (personalizzazione non applicabile, collection
  // vuota per la disciplina): `available` e' l'elenco degli step presenti nel
  // DOM, e senza argomento si ottiene la lista completa. Il contatore
  // "Passo N di T" conta solo gli step rimasti, cosi' non salta numeri.
  var STEP_KEYS = [
    "kit",
    "custom",
    "pedals",
    "accessories",
    "upgrade",
    "box",
    "review",
  ];
  var ALWAYS_STEPS = ["kit", "box", "review"];

  function stepList(mode, available) {
    var list = STEP_KEYS.filter(function (k) {
      if (!available || ALWAYS_STEPS.indexOf(k) !== -1) return true;
      return available.indexOf(k) !== -1;
    });
    return mode === "bid" ? ["bid-offer"].concat(list) : list;
  }

  function nextStep(mode, current, available) {
    var list = stepList(mode, available);
    var i = list.indexOf(current);
    if (i === -1 || i === list.length - 1) return current;
    return list[i + 1];
  }

  function previousStep(mode, current, available) {
    var list = stepList(mode, available);
    var i = list.indexOf(current);
    if (i === -1) return current;
    if (i === 0) return list[0];
    return list[i - 1];
  }

  // Addon di officina del blocco Personalizzazione, nell'ordine delle card
  // (Figma: pedivella, pacco pignoni, guarnitura; poi i componenti aggiunti
  // il 20/10/2026: power meter, corone, dischi, movimento centrale). Sono le
  // chiavi di state.addons e i valori di data-cfg-addon nel markup.
  // Nome del kit con la parola "Kit" davanti ("Kit Plus"), senza
  // raddoppiarla se il titolo la contiene gia'.
  function kitName(prefix, title) {
    if (!prefix || !title) return title || prefix || "";
    return title.toLowerCase().indexOf(prefix.toLowerCase()) === 0 ? title : prefix + " " + title;
  }

  var ADDON_KEYS = [
    "crank",
    "cassette",
    "crankset",
    "rotors",
    "chainrings",
    "powermeter",
    "bb",
    "film",
  ];
  // Solo gli addon d'officina dello step Personalizzazione stanno dietro il
  // kit Plus. La pellicola adesiva (step Box) no: e' un accessorio spedito
  // nella box, quindi il cambio kit non deve toglierla.
  var CUSTOM_ADDON_KEYS = ADDON_KEYS.filter(function (k) {
    return k !== "film";
  });

  // `description` di una risposta /cart/add.js rifiutata (tipicamente 422,
  // esaurito): arriva gia' nella lingua dello store. undefined se il corpo
  // non e' JSON, cosi' showError() ricade sull'errore generico.
  async function cartErrorDescription(res) {
    try {
      var body = await res.json();
      return (body && body.description) || undefined;
    } catch (_) {
      return undefined;
    }
  }

  // Dopo un commit riuscito la CTA resta spenta finche' la pagina cambia; se
  // la navigazione viene interrotta (Stop, Esc) senza questo limite resterebbe
  // spenta fino al reload.
  var LEAVE_TIMEOUT_MS = 10000;

  // Varianti addon scelte, in ordine fisso: payload deterministico per
  // /cart/add (acquisto e instant checkout) e per il bid.
  function addonVariantIds(state) {
    var addons = (state && state.addons) || {};
    return ADDON_KEYS.map(function (k) {
      return addons[k];
    }).filter(Boolean);
  }

  // Valore degli extra per l'evento "configurator completed" (Bikeroom
  // margina su kit, box, addon di officina e accessori, non sulla bici). Nato
  // come metrica primaria dell'A/B configurator-7-steps (esperimento PostHog
  // 100822, chiuso il 14/10/2026), resta per monitorare il valore degli extra.
  // Legge il breakdown che updateSummary ha appena mostrato nel footer, quindi
  // coincide con quello che il cliente ha visto. Valori in unita' della
  // valuta (EUR), non centesimi: e' cio' che PostHog somma nelle metriche
  // "sum". Rider Protection resta FUORI da extras_value: la applica il tema
  // in automatico (3% + fisso), non e' una scelta del cliente; viaggia a parte.
  function extrasProps(el) {
    var b = (el && el._lastBreakdown) || {};
    var cents = function (v) {
      return v > 0 ? v : 0;
    };
    var kit = cents(b.kit);
    var box = cents(b.box);
    var addons = cents(b.addons);
    var upsells = cents(b.upsells);
    var extras = kit + box + addons + upsells;
    return {
      extras_value: extras / 100,
      extras_kit_value: kit / 100,
      extras_box_value: box / 100,
      extras_addons_value: addons / 100,
      extras_upsells_value: upsells / 100,
      has_paid_extras: extras > 0,
      protection_value: cents(b.protection) / 100,
      bike_value: cents(b.bike) / 100,
      currency:
        (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) ||
        undefined,
    };
  }

  function buildAddItems(state) {
    var bike = String(state.bikeVariantId);
    var props = { __bkrm_drawer_select: "true", __bkrm_parent_variant: bike };
    var items = [
      { id: Number(state.bikeVariantId), quantity: 1 },
      { id: Number(state.kitVariantId), quantity: 1, properties: props },
      { id: Number(state.boxVariantId), quantity: 1, properties: props },
    ];
    // Addon: opzionali, quindi entrano nel carrello solo se scelti. Stesse
    // properties di kit/box, cosi' la riga resta agganciata alla sua bici nel
    // recap del carrello e nelle regole di rimozione.
    addonVariantIds(state).forEach(function (id) {
      items.push({ id: Number(id), quantity: 1, properties: props });
    });
    return items;
  }

  function isConfigComplete(state) {
    return Boolean(state && state.kitVariantId && state.boxVariantId);
  }

  // Status su cui vale la pena un secondo tentativo dell'offerta: arrivano
  // tipicamente dal davanti (Cloudflare/proxy) prima che il backend veda la
  // richiesta, quindi ritentare non rischia di duplicare l'offerta. 504 NON è
  // qui di proposito — vedi il commento in _placeBid.
  var BID_RETRY_STATUSES = [429, 502, 503];
  var BID_RETRY_DELAY_MS = 800;

  // Estrae codice e messaggio da un corpo d'errore di bike-seller senza
  // conoscerne il formato: il backend è esterno e ha risposto storicamente sia
  // in stile REST ({message}/{error}) sia in stile GraphQL Shopify
  // (errors[]/userErrors[] dentro draftOrderCreate). Meglio un'estrazione
  // difensiva che perdere l'unica riga utile della segnalazione.
  function extractBidError(body) {
    var out = { code: null, message: null };
    if (!body || typeof body !== "object") return out;
    var draft = body.data && body.data.draftOrderCreate;
    var list =
      (Array.isArray(body.errors) && body.errors) ||
      (Array.isArray(body.userErrors) && body.userErrors) ||
      (draft && Array.isArray(draft.userErrors) && draft.userErrors) ||
      null;
    var first = list && list.length ? list[0] : null;
    out.code = body.code || body.errorCode || (body.error && body.error.code) || null;
    out.message =
      body.message ||
      body.error_description ||
      (typeof body.error === "string" ? body.error : body.error && body.error.message) ||
      (typeof first === "string" ? first : first && first.message) ||
      null;
    return out;
  }

  // I corpi d'errore finiscono in PostHog: cappa la lunghezza (un 502 di
  // proxy risponde con una pagina HTML intera), redigi le email (il backend
  // può fare eco al draft order, che contiene quella del cliente) e non far
  // mai fallire il report per un corpo non serializzabile.
  function truncateForAnalytics(body, limit) {
    if (body == null) return null;
    var max = limit || 400;
    var text;
    try {
      text = typeof body === "string" ? body : JSON.stringify(body);
    } catch (e) {
      text = String(body);
    }
    if (typeof text !== "string") return null;
    text = text.replace(/[^\s"',;:<>()[\]{}]+@[^\s"',;:<>()[\]{}]+\.[A-Za-z]{2,}/g, "[email]");
    return text.length > max ? text.slice(0, max) + "…" : text;
  }

  // storefrontLoginUrl: a login that comes BACK to the storefront instead of
  // dumping the customer on the hosted account page.
  //
  // The header account link (.thb-mobile-account-link) points at
  // account.bike-room.com — the New Customer Accounts app. Signing in there
  // lands on Orders/Profile ("Welcome … Ready to shop?"), i.e. outside the
  // funnel: the customer has to press "Shop now" and walk back through a
  // couple of redirects before seeing the bike again (Asana 1217637687611434).
  // The bkrmBidResume net in theme.liquid can't help, because it only runs on
  // theme pages and the account app isn't one.
  //
  // /customer_authentication/login?return_to=<relative path> is Shopify's
  // documented route for keeping the customer in the storefront: it 302s to
  // the same OIDC authorize endpoint, but with redirect_uri pointing at
  // <shop>/customer_authentication/callback, which then honours return_to.
  // return_to only accepts relative URLs.
  //
  // Returns null when the shop is NOT on New Customer Accounts: there the
  // account link is a relative /account/login (classic) that already honours
  // return_to, and /customer_authentication/login answers 401 — see the
  // Auction Now rewrite in snippets/auction-now-overrides.liquid. Callers fall
  // back to the href they already had.
  function storefrontLoginUrl(returnTo, accountHref) {
    if (!/^https?:/i.test(accountHref || "")) return null;
    var root =
      (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
    if (root.charAt(root.length - 1) !== "/") root += "/";
    return (
      root + "customer_authentication/login?return_to=" + encodeURIComponent(returnTo)
    );
  }

  window.BkrmConfigurator = {
    parsePriceCents: parsePriceCents,
    formatCents: formatCents,
    formatOfferGroup: formatOfferGroup,
    parseOfferGroup: parseOfferGroup,
    storefrontLoginUrl: storefrontLoginUrl,
    buildAddItems: buildAddItems,
    addonVariantIds: addonVariantIds,
    ADDON_KEYS: ADDON_KEYS,
    isConfigComplete: isConfigComplete,
    STEP_KEYS: STEP_KEYS,
    stepList: stepList,
    nextStep: nextStep,
    previousStep: previousStep,
  };

  if (!customElements.get("bike-configurator")) {
    customElements.define(
      "bike-configurator",
      class BikeConfigurator extends HTMLElement {
        connectedCallback() {
          // Portal to <body>. The overlay is server-rendered inside the PDP
          // sticky info column (.product-information-sticky has
          // position:sticky + overflow-y:auto + max-height), which clips/constrains
          // our position:fixed panel so it can't cover the viewport. Moving the
          // element to be a direct child of <body> (like the old cart-drawer was)
          // makes the fixed panel/backdrop position against the viewport.
          // appendChild re-triggers connectedCallback with parentNode === body,
          // where the real init below runs; this first pass returns early.
          if (this.parentNode && this.parentNode !== document.body) {
            document.body.appendChild(this);
            return;
          }

          this.state = {
            kitVariantId: undefined,
            boxVariantId: undefined,
            addons: {},
          };
          this._addonChoices = {};
          this._customUpgraded = false;
          this._customUpgradeFrom = "base";
          this._submitting = false;
          // Upsells added via <quick-view> during THIS open session, keyed by
          // variant id → { title, cents }. Used to reflect accessories (pedals,
          // etc.) in the price recap. See onCartAdded().
          this._sessionExtras = {};
          this._extrasCents = 0;
          this.proceedBtn = this.querySelector(".bike-configurator__proceed");
          this.errorEl = this.querySelector(".bike-configurator__error");
          this._proceedLabel = this.proceedBtn ? this.proceedBtn.textContent : "";
          this._onClick = this.onClick.bind(this);
          this.addEventListener("click", this._onClick);
          // Dopo un commit riuscito la CTA resta spenta durante il redirect
          // (_holdSubmitWhileLeaving). Se il cliente torna indietro e la
          // pagina esce dal bfcache, la riattiva.
          var pageSelf = this;
          this._onPageShow = function (e) {
            if (e.persisted && pageSelf._leaving) pageSelf._releaseSubmit();
          };
          window.addEventListener("pageshow", this._onPageShow);
          // Click sulle card upsell (quick-view): NON deve mai aprire la modale
          // prodotto. Capture-phase: gira PRIMA del listener del custom element
          // quick-view e ne blocca la propagazione. Poi fa toggle:
          //  - bid  → nel payload dell'offerta (nessun carrello);
          //  - compra-ora → add/remove diretto sul carrello reale.
          var bidSelf = this;
          this._onUpsellCapture = function (e) {
            var card = e.target.closest("quick-view[data-upsell-variant-id]");
            if (!card || !bidSelf.contains(card)) return;
            // La select delle varianti sta DENTRO la card: aprirla non e'
            // un toggle. Si ferma solo la propagazione (la quick-view
            // aprirebbe la modale prodotto), il default resta al browser.
            if (e.target.closest("[data-cfg-variant-field]")) {
              e.stopPropagation();
              return;
            }
            e.preventDefault();
            e.stopPropagation();
            bidSelf._toggleUpsellCard(card);
          };
          this.addEventListener("click", this._onUpsellCapture, true);
          // Scelta della variante sulla card: la card "diventa" quella
          // variante (id, prezzo, etichetta) e da li' in poi l'add usa lei.
          this._onVariantPick = function (e) {
            // Select di un addon gia' aggiunto (es. finitura, dentatura):
            // cambiarla sostituisce la scelta. Prima restava la vecchia e
            // "Rimuovi" la toglieva invece di aggiornarla.
            var addonSel = e.target.closest("[data-cfg-addon-select]");
            if (addonSel && bidSelf.contains(addonSel)) {
              var aCard = addonSel.closest("[data-cfg-addon]");
              var aKey = aCard && aCard.dataset.cfgAddon;
              var aOpt = addonSel.options[addonSel.selectedIndex];
              if (
                aKey &&
                aOpt &&
                aOpt.value &&
                bidSelf.state.addons[aKey] &&
                bidSelf.state.addons[aKey] !== aOpt.value &&
                aCard.querySelector('option[value="' + bidSelf.state.addons[aKey] + '"]')
              ) {
                bidSelf._chooseAddon(aKey, aOpt);
                bidSelf.updateSummary();
              }
              return;
            }
            var select = e.target.closest("[data-cfg-upsell-variant]");
            if (!select || !bidSelf.contains(select)) return;
            var pickCard = select.closest("quick-view");
            if (pickCard) bidSelf._applyCardVariant(pickCard, select);
          };
          this.addEventListener("change", this._onVariantPick);
          // Esc sulla conferma "senza pedali" = "Scegli i pedali".
          this._onKeydown = function (e) {
            if (e.key !== "Escape") return;
            var dlg = bidSelf._pedalsConfirmEl();
            if (dlg && !dlg.hidden) {
              e.stopPropagation();
              bidSelf._hidePedalsConfirm();
            }
          };
          this.addEventListener("keydown", this._onKeydown);
          this._initBidMode();
          // Turn on scroll-snap for an upsell carousel only once it has seen a
          // real touch gesture (see .cd-upsell-touch-active in
          // product-configurator.css). A mouse click on the arrow buttons
          // already scrolls exactly one card (upsellScrollStep()), so it never
          // needs snapping; leaving scroll-snap-type on unconditionally let it
          // fight the click's own scrollBy animation on some engines (video
          // review, 2026-07-02 — the jump only ever happened on arrow clicks).
          this._onTouchStart = function (e) {
            var list = e.target.closest(".cart-drawer-upsell-list");
            if (list) list.classList.add("cd-upsell-touch-active");
          };
          this.addEventListener("touchstart", this._onTouchStart, { passive: true });
          // Wire the subtotal toggle button (expand/collapse breakdown).
          var toggleBtn = this.querySelector(".bike-configurator__summary-total-toggle");
          if (toggleBtn) {
            this._onToggleSummary = this.toggleSummary.bind(this);
            toggleBtn.addEventListener("click", this._onToggleSummary);
          }
          this.syncProceed();
          this._onProceed = this.proceed.bind(this);
          if (this.proceedBtn)
            this.proceedBtn.addEventListener("click", this._onProceed);
          // Reflect upsell adds in the summary. Fired by product.js / app.js
          // after every successful /cart/add.js from the quick-view modal.
          this._onCartAdded = this.onCartAdded.bind(this);
          document.addEventListener("cart:item-added", this._onCartAdded);
          // Keep the configurator in sync with the PDP variant selector: a new
          // size/color is effectively a different bike, so update id/price/
          // protection/chips and reset any prior service/box selection.
          this._onVariantChange = this.onVariantChange.bind(this);
          document.addEventListener(
            "product:variant-change",
            this._onVariantChange,
          );

          // Row interaction helpers ported from the old cart-drawer.liquid IIFE.
          // Scoped to this element to avoid leaking to unrelated drawers.
          var self = this;

          // equalizeServiceDescHeights: levels the min-height of service tier
          // descriptions so checklists don't "jump" position when switching tabs.
          // Ported from cart-drawer.liquid lines 1160–1186.
          function equalizeServiceDescHeights() {
            self
              .querySelectorAll('[data-cd-config-group="service-pack"]')
              .forEach(function (section) {
                var descs = section.querySelectorAll(
                  ".cd-config-row-details .cd-config-row-desc",
                );
                if (descs.length < 2) return;
                var max = 0;
                descs.forEach(function (d) {
                  d.style.minHeight = "";
                });
                descs.forEach(function (d) {
                  max = Math.max(max, d.offsetHeight);
                });
                if (max > 0)
                  descs.forEach(function (d) {
                    d.style.minHeight = max + "px";
                  });
              });
          }
          this._equalizeServiceDescHeights = equalizeServiceDescHeights;

          // scrollToServiceTabsIfMobile: after an off-item click, scroll the
          // service-pack section into view so the newly selected tab is visible.
          // Ported from cart-drawer.liquid lines 1271–1284.
          function scrollToServiceTabsIfMobile(setEl) {
            if (
              window.matchMedia &&
              window.matchMedia("(min-width: 768px)").matches
            )
              return;
            var section = (setEl || self).querySelector(
              '.cd-config-section[data-cd-config-group="service-pack"]',
            );
            var target = section || self;
            if (typeof target.scrollIntoView === "function") {
              target.scrollIntoView({ behavior: "smooth", block: "start" });
            }
          }

          // flashSelectedTab: brief CSS animation on the tab that was just
          // selected (visible feedback when the tab is off-screen on mobile).
          // Ported from cart-drawer.liquid lines 1254–1264.
          function flashSelectedTab(group, variantId) {
            if (group !== "service-pack" || !variantId) return;
            self
              .querySelectorAll(
                '.cd-config-row-main[data-cd-config-group="' +
                  group +
                  '"][data-variant-id="' +
                  variantId +
                  '"]',
              )
              .forEach(function (m) {
                m.classList.remove("cd-config-row-main--just-selected");
                void m.offsetWidth; // force reflow to restart animation
                m.classList.add("cd-config-row-main--just-selected");
              });
          }
          this._flashSelectedTab = flashSelectedTab;
          this._scrollToServiceTabsIfMobile = scrollToServiceTabsIfMobile;

          // Run once on connect so descriptions are already leveled when the
          // overlay first opens. Also equalize on resize (debounced).
          equalizeServiceDescHeights();
          var _cdDescResizeTimer;
          this._onResize = function () {
            clearTimeout(_cdDescResizeTimer);
            _cdDescResizeTimer = setTimeout(equalizeServiceDescHeights, 150);
          };
          window.addEventListener("resize", this._onResize);
        }

        // Toggle the subtotal breakdown open/closed.
        toggleSummary() {
          var summary = this.querySelector(".bike-configurator__summary");
          var toggleBtn = this.querySelector(".bike-configurator__summary-total-toggle");
          if (!summary) return;
          var expanded = summary.classList.toggle("bike-configurator__summary--expanded");
          if (toggleBtn) toggleBtn.setAttribute("aria-expanded", expanded ? "true" : "false");
        }

        // Collapse the subtotal breakdown (Figma: ogni step entra col
        // sommario chiuso; l'espansione è solo on-demand via toggle — l'auto
        // -expand a ogni selezione è stato rimosso su richiesta di Alberto).
        // Il bid è escluso dai caller: openBid() lo apre di proposito.
        _collapseSummary() {
          var summary = this.querySelector(".bike-configurator__summary");
          var toggleBtn = this.querySelector(".bike-configurator__summary-total-toggle");
          if (summary) summary.classList.remove("bike-configurator__summary--expanded");
          if (toggleBtn) toggleBtn.setAttribute("aria-expanded", "false");
        }

        disconnectedCallback() {
          if (this._onClick) this.removeEventListener("click", this._onClick);
          if (this._onUpsellCapture)
            this.removeEventListener("click", this._onUpsellCapture, true);
          if (this._onVariantPick)
            this.removeEventListener("change", this._onVariantPick);
          if (this._onKeydown)
            this.removeEventListener("keydown", this._onKeydown);
          if (this._onTouchStart)
            this.removeEventListener("touchstart", this._onTouchStart);
          if (this._onProceed && this.proceedBtn)
            this.proceedBtn.removeEventListener("click", this._onProceed);
          var toggleBtn = this.querySelector(".bike-configurator__summary-total-toggle");
          if (toggleBtn && this._onToggleSummary)
            toggleBtn.removeEventListener("click", this._onToggleSummary);
          if (this._onResize)
            window.removeEventListener("resize", this._onResize);
          if (this._onPageShow)
            window.removeEventListener("pageshow", this._onPageShow);
          clearTimeout(this._leaveTimer);
          if (this._onCartAdded)
            document.removeEventListener("cart:item-added", this._onCartAdded);
          if (this._onVariantChange)
            document.removeEventListener(
              "product:variant-change",
              this._onVariantChange,
            );
        }

        onClick(e) {
          // "Live chat" nella help card (feedback Mattia) E nel link
          // "consulenza gratuita" dell'alert di uscita: apri la chat vera
          // invece di navigare all'Help Hub, così l'utente resta nel
          // configuratore/carrello e vede ancora la bici mentre scrive. La
          // chat dello store è il widget custom Bikeroom (#br-bubble
          // launcher / #br-win finestra, script esterno bikeroom-crm) —
          // stesso pattern di apertura di mkt-media-text.liquid. Il launcher
          // è display:none mentre il configuratore è aperto (sezione 5 del
          // CSS), ma il click programmatico funziona lo stesso; #br-win.br-open
          // ha un'eccezione CSS dedicata per mostrarsi sopra l'overlay.
          // Gorgias resta come ramo difensivo se lo store ci tornasse;
          // l'href (Help Hub, con #br-chat) è il fallback quando nessun
          // widget è caricato.
          var helpLink = e.target.closest(
            ".bike-configurator__help-link, .bike-configurator__alert-help-link",
          );
          if (helpLink) {
            var brWin = document.getElementById("br-win");
            var brBubble = document.getElementById("br-bubble");
            var gc = window.GorgiasChat;
            if (brWin && brWin.classList.contains("br-open")) {
              e.preventDefault(); // già aperta
            } else if (brBubble) {
              e.preventDefault();
              brBubble.click();
            } else if (gc && typeof gc.open === "function") {
              e.preventDefault();
              gc.open();
            }
            return;
          }
          // --- Conferma "senza pedali": prima dei controlli dell'alert di
          // uscita, perche' condivide con lui la classe dell'overlay.
          if (e.target.closest("[data-cfg-pedals-skip]")) {
            this._confirmNoPedals();
            return;
          }
          var pedalsDlg = e.target.closest("[data-cfg-pedals-confirm]");
          if (
            e.target.closest("[data-cfg-pedals-choose]") ||
            (pedalsDlg && e.target === pedalsDlg)
          ) {
            this._hidePedalsConfirm();
            return;
          }
          if (pedalsDlg) return;
          // --- Exit-confirmation alert controls (checked BEFORE the close
          // handler, since the alert sits above the panel) ---
          if (e.target.closest("[data-bike-cfg-alert-confirm]")) {
            // "Yes, exit" → dismiss alert and actually close.
            this.hideExitAlert();
            this.close();
            return;
          }
          if (e.target.closest("[data-bike-cfg-alert-dismiss]")) {
            // "Continue setup" / alert X → stay in the configurator.
            this.hideExitAlert();
            return;
          }
          // Click sull'overlay scuro dell'alert (fuori dalla card) = continua.
          if (
            e.target.classList &&
            e.target.classList.contains("bike-configurator__alert")
          ) {
            this.hideExitAlert();
            return;
          }

          // X del pannello / backdrop: chiedi conferma invece di chiudere subito.
          if (
            e.target.closest(".bike-configurator__close") ||
            e.target.closest(".bike-configurator__backdrop")
          ) {
            this.showExitAlert();
            return;
          }

          // Back chevron: previous step in the wizard.
          if (e.target.closest("[data-cfg-step-back]")) {
            this._setStep(
              window.BkrmConfigurator.previousStep(
                this.dataset.mode,
                this.dataset.step,
                this._availableSteps(),
              ),
            );
            return;
          }

          // Label della barra di avanzamento (desktop): torna a uno step gia'
          // raggiunto. I segmenti non raggiungibili sono disabled.
          var progressJump = e.target.closest("[data-cfg-progress-jump]");
          if (progressJump && !progressJump.disabled) {
            this._setStep(progressJump.dataset.cfgProgressJump);
            return;
          }

          // X nel riepilogo: toglie un extra opzionale senza tornare al suo
          // step (handoff, sezione 8). Bici, protezione, kit e box non hanno
          // la X: si cambiano dal loro step.
          var removeBtn = e.target.closest("[data-cfg-summary-remove]");
          if (removeBtn && this.contains(removeBtn)) {
            e.preventDefault();
            if (removeBtn.dataset.cfgSummaryRemove === "addon") {
              this._clearAddon(removeBtn.dataset.addonKey);
              this.updateSummary();
            } else {
              this._removeExtra(removeBtn.dataset.variantId);
            }
            return;
          }

          // Filtri a pillola dello step pedali: solo visibilita', la scelta
          // fatta resta anche se il filtro la nasconde.
          var pedalFilter = e.target.closest("[data-cfg-pedal-filter]");
          if (pedalFilter && this.contains(pedalFilter)) {
            this._applyPedalFilter(pedalFilter.dataset.cfgPedalFilter);
            return;
          }

          // Stessi filtri nello step Personalizzazione (trasmissione /
          // componenti): anche qui solo visibilita'.
          var customFilter = e.target.closest("[data-cfg-custom-filter]");
          if (customFilter && this.contains(customFilter)) {
            this._applyCustomFilter(customFilter.dataset.cfgCustomFilter);
            return;
          }

          // Step a sezioni (Accessori, Migliora): le chip mostrano una sezione
          // sola, e solo nello step in cui stanno.
          var accFilter = e.target.closest("[data-cfg-acc-filter]");
          if (accFilter && this.contains(accFilter)) {
            this._applyAccFilter(
              accFilter.dataset.cfgAccFilter,
              accFilter.closest("[data-cfg-step-panel]"),
            );
            return;
          }

          // Review-step recap rows: jump back to the step that produced them.
          // Inert outside the review step (no-op click on kit/box steps).
          var jumpEl = e.target.closest("[data-cfg-summary-jump]");
          if (jumpEl && this.dataset.step === "review") {
            this._setStep(jumpEl.dataset.cfgSummaryJump);
            return;
          }

          // Review-step cart-footer actions (non-bid only; markup is absent
          // in bid mode's review step, see product-configurator.liquid):
          // "add" commits and lands on /cart, "checkout" commits and goes
          // straight to Shopify checkout.
          var cartAction = e.target.closest("[data-cfg-cart-action]");
          if (cartAction) {
            this._commitAndRedirect(cartAction.dataset.cfgCartAction);
            return;
          }

          // "Specs Details": espandi/collassa il pannello specs sotto la bici.
          var specsToggle = e.target.closest(".bike-configurator__specs-toggle");
          if (specsToggle) {
            var specsExpanded =
              specsToggle.getAttribute("aria-expanded") === "true";
            specsToggle.setAttribute(
              "aria-expanded",
              specsExpanded ? "false" : "true",
            );
            var specsBody = specsToggle.parentElement.querySelector(
              ".bike-configurator__specs-body",
            );
            if (specsBody) {
              if (specsExpanded) specsBody.setAttribute("hidden", "");
              else specsBody.removeAttribute("hidden");
            }
            return;
          }

          // Upsell carousel scroller arrows: look up the list relative to the
          // button so this works with multiple configurators on the cart page
          // (no getElementById — IDs like cd-upsell-list-1-<variantId> are unique
          // per bike but we don't need them here).
          var scrollerLeft = e.target.closest(".cd-upsell-scroller-left");
          if (scrollerLeft) {
            var listL = scrollerLeft.closest(".cart-drawer-upsell-container");
            if (listL) {
              var listElL = listL.querySelector(".cart-drawer-upsell-list");
              if (listElL) upsellScrollToCard(listElL, -1);
            }
            return;
          }
          var scrollerRight = e.target.closest(".cd-upsell-scroller-right");
          if (scrollerRight) {
            var listR = scrollerRight.closest(".cart-drawer-upsell-container");
            if (listR) {
              var listElR = listR.querySelector(".cart-drawer-upsell-list");
              if (listElR) upsellScrollToCard(listElR, 1);
            }
            return;
          }

          // D3: click on a greyed-out service feature item → select the
          // cheapest tier that includes it. data-cd-off-item-targets holds
          // pipe-separated variant ids; pick the first (cheapest listed).
          // Ported from cart-drawer.liquid lines 1540–1567.
          var offItem = e.target.closest(".cd-config-row-check--off");
          if (
            offItem &&
            offItem.dataset.cdOffItemTargets &&
            offItem.closest('[data-cd-config-group="service-pack"]')
          ) {
            e.preventDefault();
            var targets = offItem.dataset.cdOffItemTargets
              .split("|")
              .map(function (v) {
                return String(v).trim();
              })
              .filter(Boolean);
            if (targets.length) {
              var targetVid = targets[0];
              this.state.kitVariantId = targetVid;
              var fakeRow = this.querySelector(
                '.cd-config-row-main[data-cd-config-group="service-pack"][data-variant-id="' +
                  targetVid +
                  '"]',
              );
              if (fakeRow) this.markSelected("service-pack", fakeRow);
              if (this._flashSelectedTab)
                this._flashSelectedTab("service-pack", targetVid);
              if (this._scrollToServiceTabsIfMobile)
                this._scrollToServiceTabsIfMobile(this);
              if (this._equalizeServiceDescHeights)
                setTimeout(this._equalizeServiceDescHeights, 0);
              this.syncProceed();
              this.updateSummary();
            }
            return;
          }

          // Card addon (design Figma 6821-15317): select della variante +
          // bottone Add, non una riga radio come kit e box. Un secondo click
          // toglie la scelta: e' un ricambio a pagamento su una bici che il
          // componente ce l'ha gia', quindi deve restare sempre annullabile.
          // Checkbox "Passa al kit Plus": compare solo col kit Base ed e' il
          // solo modo di sbloccare le card senza tornare allo step del kit.
          // Sul <label> il click arriva due volte (label + input): si
          // ascolta solo l'input, altrimenti la spunta si annulla da sola.
          var customUp = e.target.closest("[data-cfg-custom-upgrade-box]");
          if (customUp && this.contains(customUp)) {
            this._toggleCustomUpgrade(customUp.checked);
            return;
          }

          var addonAdd = e.target.closest("[data-cfg-addon-add]");
          if (addonAdd && this.contains(addonAdd)) {
            e.preventDefault();
            this._toggleAddon(addonAdd);
            return;
          }

          var row = e.target.closest(".cd-config-row-main");
          if (!row) {
            // Box cards (Standard/Safe/Eco): the whole card is clickable,
            // not just the radio+title+price button - a click anywhere in
            // it (e.g. on the description text below) still selects it.
            var boxCard = e.target.closest(".cd-config-row");
            if (boxCard && this.contains(boxCard)) {
              row = boxCard.querySelector(
                '.cd-config-row-main[data-cd-config-group="box"]',
              );
            }
          }
          if (!row || !this.contains(row)) return;
          var group = row.dataset.cdConfigGroup;
          var variantId = row.dataset.variantId;
          if (group === "service-pack") this.state.kitVariantId = variantId;
          else if (group === "box") this.state.boxVariantId = variantId;
          else return;
          this._trackBidSelection(group, variantId);
          this.markSelected(group, row);
          // Il kit decide se la personalizzazione spetta o no: il blocco
          // dentro lo step upsell si mostra o si nasconde qui.
          if (group === "service-pack") {
            // Scelta manuale del kit: annulla l'upgrade fatto dalla
            // checkbox, altrimenti resterebbe spuntata su un kit scelto a
            // mano. _customUpgradeFrom NON si tocca: e' il kit da cui si fa
            // l'upgrade (Base), una costante — assegnarci l'handle appena
            // cliccato rendeva "upgradabile" qualunque kit, Pro compreso.
            this._customUpgraded = false;
            this._syncCustomBlock();
          }
          if (this._flashSelectedTab) this._flashSelectedTab(group, variantId);
          if (this._equalizeServiceDescHeights)
            setTimeout(this._equalizeServiceDescHeights, 0);
          this.syncProceed();
          this.updateSummary();
        }

        _trackBidSelection(group, variantId) {
          if (this.dataset.mode !== "bid") return;
          var EVENTS = {
            "service-pack": "bid kit selected",
            box: "bid box selected",
            crank: "bid crank selected",
          };
          if (!EVENTS[group]) return;
          track(
            EVENTS[group],
            {
              variant_id: String(this.dataset.bikeVariantId),
              selection_variant_id: String(variantId),
            }
          );
        }

        // row === null deseleziona tutto il gruppo (solo lo step pedivella,
        // l'unico gruppo che ammette "nessuna scelta" — kit e box sono
        // obbligatori e passano sempre una riga).
        markSelected(group, row) {
          this.querySelectorAll(
            '.cd-config-row-main[data-cd-config-group="' + group + '"]',
          ).forEach(function (b) {
            b.setAttribute("aria-pressed", "false");
            var card = b.closest(".cd-config-row");
            if (card) card.classList.remove("cd-config-row--selected");
          });
          if (!row) return;
          row.setAttribute("aria-pressed", "true");
          var sel = row.closest(".cd-config-row");
          if (sel) sel.classList.add("cd-config-row--selected");
        }

        syncProceed() {
          if (!this.proceedBtn) return;
          var ok = !this._submitting;
          var step = this.dataset.step;
          if (ok) {
            if (this.dataset.mode === "bid") {
              if (step === "bid-offer") ok = this._bidOfferOk === true;
              else if (step === "kit") ok = Boolean(this.state.kitVariantId);
              else if (step === "box") ok = Boolean(this.state.boxVariantId);
              else if (step !== "review") ok = true;
              else
                ok =
                  this._bidOfferOk === true &&
                  window.BkrmConfigurator.isConfigComplete(this.state);
            } else if (step === "kit") {
              ok = Boolean(this.state.kitVariantId);
            } else if (step === "box") {
              ok = Boolean(this.state.boxVariantId);
            } else if (step && step !== "review") {
              // Step prodotto (personalizzazione, pedali, accessori,
              // migliora): vengono PRIMA della box, quindi non possono
              // pretendere la configurazione completa. Il "Continua" dei
              // pedali resta attivo di proposito: e' il click a mostrare
              // l'errore inline, un bottone spento non spiegherebbe nulla.
              ok = true;
            } else {
              // review, o undefined (chiamanti legacy che non settano
              // dataset.step) — serve la configurazione completa.
              ok = window.BkrmConfigurator.isConfigComplete(this.state);
            }
          }
          this.proceedBtn.disabled = !ok;
        }

        hideError() {
          if (this.errorEl) this.errorEl.setAttribute("hidden", "");
        }

        // `message` opzionale: senza argomento resta l'errore generico di
        // sempre, così tutti i chiamanti esistenti non cambiano.
        showError(message) {
          if (!this.errorEl) return;
          this.errorEl.textContent =
            message || this.dataset.errorText || "Something went wrong, please try again.";
          this.errorEl.removeAttribute("hidden");
        }

        async proceed() {
          // Double-submit guard (Fix 2a).
          if (this._submitting) return;

          if (this.dataset.mode === "bid") {
            await this._onBidProceed();
            return;
          }

          // Step intermedi: avanti di uno (con la validazione pedali). Il
          // commit parte solo dal review, o senza data-step (chiamanti
          // legacy che non usano il wizard).
          var step = this.dataset.step;
          if (step && step !== "review") {
            this._advanceFrom(step);
            return;
          }

          await this._commitAndRedirect();
        }

        // The atomic add-to-cart (or edit-mode swap) commit. Reached from the
        // review step (non-bid wizard) and directly from edit mode / any
        // legacy caller that never set dataset.step.
        // redirectTo: "cart" (default, e.g. the review step's "Add to cart")
        // or "checkout" (the review step's "Go to checkout" - commits the
        // same atomic add, then goes straight to Shopify checkout instead of
        // stopping at /cart).
        async _commitAndRedirect(redirectTo) {
          // Double-submit guard (Fix 2a): il path click [data-cfg-cart-action]
          // arriva qui senza passare da proceed() — senza guard un double-tap
          // su "Add to cart" (o Add to cart + Go to checkout col primo POST in
          // volo) lancia due /cart/add.js concorrenti = righe duplicate.
          if (this._submitting) return;
          if (!window.BkrmConfigurator.isConfigComplete(this.state)) return;

          this._submitting = true;
          if (this.proceedBtn) this.proceedBtn.disabled = true;
          this.querySelectorAll(".bike-configurator__cart-action").forEach(
            function (b) { b.disabled = true; },
          );
          this.hideError();

          var root =
            (window.Shopify &&
              window.Shopify.routes &&
              window.Shopify.routes.root) ||
            "/";
          var cartBefore = null;
          try {
            if (this.dataset.editMode === "true") {
              await this._commitEdit(root);
            } else {
              // Prima gli accessori spariti, poi la bici: se uno non rientra
              // (esaurito nel frattempo) il cliente lo vede qui, col totale
              // aggiornato, e la bici non e' ancora in carrello, quindi un
              // secondo "Procedi" non la duplica.
              var restored = await this._restoreSessionExtras(root);
              if (restored.failed.length) {
                this._stopOnFailedExtras(restored.failed, "configurator restore failed");
                return;
              }
              cartBefore = restored.cart;
              var items = window.BkrmConfigurator.buildAddItems(this.cartState());
              var res = await fetch(root + "cart/add.js", {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Accept: "application/json",
                },
                body: JSON.stringify({ items: items }),
              });
              if (!res.ok) {
                // Tipicamente 422, bici venduta nel frattempo: il motivo di
                // Shopify lo dice, l'errore generico no. Gli accessori appena
                // rimessi restano agganciati a una bici che non c'e': li
                // toglie la pulizia orfani al prossimo caricamento.
                var refusal = new Error("cart/add failed: " + res.status);
                refusal.description = await cartErrorDescription(res);
                throw refusal;
              }
            }
            // Associa gli accessori della sessione alla bici appena
            // committata (o già in carrello, in edit): best-effort, un
            // fallimento qui non deve bloccare il redirect. Riusa il
            // carrello letto dal ripristino: aggiungere la bici non cambia
            // le righe degli accessori.
            await this._tagSessionExtrasToBike(root, cartBefore);
            track('configurator completed', Object.assign({
              bike_variant_id: this.dataset.bikeVariantId,
              kit_variant_id: this.state.kitVariantId || undefined,
              box_variant_id: this.state.boxVariantId || undefined,
              addon_variant_ids: addonVariantIds(this.state),
              edit_mode: this.dataset.editMode === "true",
            }, extrasProps(this)));
            this._holdSubmitWhileLeaving();
            window.location.assign(root + (redirectTo === "checkout" ? "checkout" : "cart"));
          } catch (err) {
            this.showError(err && err.description);
            this.dispatchEvent(
              new CustomEvent("bike-configurator:error", { bubbles: true }),
            );
          } finally {
            if (!this._leaving) this._releaseSubmit();
          }
        }

        // Il commit e' riuscito e la pagina sta per cambiare: le CTA restano
        // spente. Un secondo tap rilancerebbe gli add (nell'Instant buy
        // svuoterebbe il carrello mentre /checkout lo legge). Le riattivano
        // il ritorno dal bfcache (listener pageshow) o, se la navigazione
        // viene interrotta (Stop, Esc), LEAVE_TIMEOUT_MS.
        _holdSubmitWhileLeaving() {
          var self = this;
          this._leaving = true;
          clearTimeout(this._leaveTimer);
          this._leaveTimer = setTimeout(function () {
            self._releaseSubmit();
          }, LEAVE_TIMEOUT_MS);
        }

        // Riattiva le CTA dopo un commit fallito o fermato, o tornando alla
        // pagina dopo il redirect.
        _releaseSubmit() {
          clearTimeout(this._leaveTimer);
          this._leaving = false;
          this._submitting = false;
          this.querySelectorAll(".bike-configurator__cart-action").forEach(
            function (b) { b.disabled = false; },
          );
          this.syncProceed();
        }

        // Accessori che non sono entrati in carrello (ripristino al commit,
        // upsell dell'Instant buy): il "Procedi" si ferma qui. I rifiutati da
        // Shopify (esauriti) escono dal drawer, cosi' il totale torna vero e
        // il prossimo "Procedi" va avanti senza; quelli caduti per un errore
        // di rete restano e vengono riprovati. Stesso evento d'errore del
        // catch del commit, piu' un track per misurare il blocco.
        _stopOnFailedExtras(failed, trackEvent) {
          failed.forEach(function (f) {
            if (!f.refused) return;
            delete this._sessionExtras[f.id];
            if (this._bidUpsells) delete this._bidUpsells[f.id];
          }, this);
          this.renderExtras();
          var reasons = failed
            .map(function (f) {
              return f.description;
            })
            .filter(function (d, i, all) {
              return d && all.indexOf(d) === i;
            });
          this.showError(reasons.join(" ") || undefined);
          this.dispatchEvent(
            new CustomEvent("bike-configurator:error", { bubbles: true }),
          );
          track(trackEvent, {
            variant_id: String(this.dataset.bikeVariantId),
            upsell_variant_ids: failed.map(function (f) {
              return String(f.id);
            }),
            refused_variant_ids: failed
              .filter(function (f) {
                return f.refused;
              })
              .map(function (f) {
                return String(f.id);
              }),
            description: reasons.join(" ") || undefined,
          });
        }

        // Rimette in carrello gli accessori scelti in questa sessione che nel
        // frattempo sono spariti. La pulizia orfani (bkrmPruneCartOrphans in
        // app.js) gira a ogni pagina caricata e toglie gli accessori la cui
        // bici non e' ancora in carrello: anche quando quella pagina si apre
        // in un'ALTRA scheda e la configurazione qui e' ancora in corso. Il
        // drawer li mostrava come aggiunti e li contava nel totale, poi la
        // bici entrava senza di loro (riprodotto il 21/10/2026 con due
        // schede). Solo gli add di questa sessione: le righe gia' in carrello
        // all'apertura (sessionAdded:false) non si toccano.
        // Review della #247 (27/10/2026):
        // - si rimette solo cio' che la pulizia ha tolto (bkrmPrunedOrphans in
        //   localStorage, scritto da app.js), mai una riga che il cliente ha
        //   tolto a mano in un'altra scheda. Se lo storage non si legge, si
        //   rimette tutto come prima;
        // - un errore su un accessorio non salta i successivi;
        // - gli accessori che non rientrano tornano in failed (con la
        //   description di Shopify, gia' nella lingua dello store): chi chiama
        //   toglie dal drawer i rifiutati (refused) e lo dice al cliente; un
        //   errore di rete non e' un rifiuto e il record della pulizia resta,
        //   cosi' il prossimo "Procedi" lo riprova;
        // - se il carrello non si legge lancia: chi chiama mostra l'errore
        //   prima di aggiungere la bici, invece di proseguire senza accessori.
        // Restituisce { cart, failed }: cart e' il carrello letto (o null).
        async _restoreSessionExtras(root) {
          var out = { cart: null, failed: [] };
          var extras = this._sessionExtras || {};
          var ids = Object.keys(extras).filter(function (id) {
            return extras[id].sessionAdded !== false;
          });
          if (!ids.length) return out;
          var bikeVid = String(this.dataset.bikeVariantId);
          var res = await fetch(root + "cart.js", {
            headers: { Accept: "application/json" },
          });
          if (!res.ok) throw new Error("cart.js failed: " + res.status);
          out.cart = await res.json();
          var items = out.cart.items || [];
          // Presente = stessa riga (line key), oppure stessa variante non
          // associata a un'altra bici.
          var mancanti = ids.filter(function (id) {
            var x = extras[id];
            return !items.some(function (it) {
              if (x.key && it.key === x.key) return true;
              if (String(it.variant_id) !== String(id)) return false;
              var parent = it.properties && it.properties.__bkrm_parent_variant;
              return !parent || String(parent) === bikeVid;
            });
          });
          if (!mancanti.length) return out;

          // null = storage non leggibile (o valore non valido): si rimette tutto.
          var pruned = null;
          try {
            pruned = JSON.parse(localStorage.getItem("bkrmPrunedOrphans") || "[]");
          } catch (e) {
            pruned = null;
          }
          if (!Array.isArray(pruned)) pruned = null;
          var readAt = Date.now();
          var daRimettere = mancanti.filter(function (id) {
            // Solo una pulizia avvenuta DOPO l'add di questa sessione: un
            // record rimasto da una visita precedente (si tengono fino a un
            // giorno) non deve rimettere una riga tolta a mano adesso.
            var addedAt = extras[id].addedAt || 0;
            return (
              !pruned ||
              pruned.some(function (e) {
                return e && e.v === String(id) && e.p === bikeVid && e.t >= addedAt;
              })
            );
          });
          // Gli altri li ha tolti il cliente: il drawer smette di contarli.
          mancanti.forEach(function (id) {
            if (daRimettere.indexOf(id) === -1) delete extras[id];
          });
          out.failed = (await this._addExtrasToBike(root, daRimettere)).failed;
          // Via dallo storage solo i record degli accessori rimessi o
          // rifiutati qui; quelli caduti per un errore di rete restano per il
          // prossimo "Procedi". Si rilegge subito prima di scrivere e si
          // tolgono solo record gia' letti prima degli add (t <= readAt):
          // un'altra scheda puo' averne scritti nel frattempo, anche per lo
          // stesso accessorio della stessa bici, e quelli restano.
          var retryIds = out.failed
            .filter(function (f) {
              return !f.refused;
            })
            .map(function (f) {
              return String(f.id);
            });
          var chiusi = daRimettere.filter(function (id) {
            return retryIds.indexOf(String(id)) === -1;
          });
          if (chiusi.length) {
            try {
              var latest = JSON.parse(localStorage.getItem("bkrmPrunedOrphans") || "[]");
              if (Array.isArray(latest)) {
                localStorage.setItem(
                  "bkrmPrunedOrphans",
                  JSON.stringify(
                    latest.filter(function (e) {
                      return !(
                        e &&
                        e.p === bikeVid &&
                        e.t <= readAt &&
                        chiusi.indexOf(e.v) !== -1
                      );
                    }),
                  ),
                );
              }
            } catch (e) {
              /* best-effort */
            }
          }
          return out;
        }

        // Aggiunge al carrello gli accessori `ids` di _sessionExtras, uno per
        // uno e agganciati alla bici (__bkrm_parent_variant): un errore su uno
        // non salta i successivi. Lo usano il ripristino al commit e gli
        // upsell dell'Instant buy. Restituisce { failed }: refused = rifiuto
        // di Shopify (es. esaurito, con la sua description), altrimenti
        // errore di rete.
        async _addExtrasToBike(root, ids) {
          var extras = this._sessionExtras || {};
          var bikeVid = String(this.dataset.bikeVariantId);
          var failed = [];
          for (var i = 0; i < ids.length; i++) {
            var id = ids[i];
            var x = extras[id] || {};
            try {
              var resAdd = await fetch(root + "cart/add.js", {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Accept: "application/json",
                },
                body: JSON.stringify({
                  items: [
                    {
                      id: id,
                      quantity: x.qty || 1,
                      properties: { __bkrm_parent_variant: bikeVid },
                    },
                  ],
                }),
              });
              if (!resAdd.ok) {
                failed.push({
                  id: id,
                  title: x.title,
                  description: await cartErrorDescription(resAdd),
                  refused: true,
                });
                continue;
              }
              var added = await resAdd.json();
              var line = added.items ? added.items[0] : added;
              if (line && line.key && extras[id]) extras[id].key = line.key;
            } catch (e) {
              failed.push({ id: id, title: x.title, refused: false });
            }
          }
          return { failed: failed };
        }

        // Associa al parent-variant della bici tutte le righe carrello degli
        // accessori scelti in questa sessione (__bkrm_parent_variant): la cart
        // page le raggruppa sotto la bici (riga ACCESSORIO) e la delete
        // cascade di cart.js le rimuove insieme a lei — mai orfane. Regole:
        // - solo righe che matchano _sessionExtras (per line-key o variant id);
        // - MAI ri-parentare una riga già associata (multi-bici: un accessorio
        //   di un'altra bici non viene rubato);
        // - properties esistenti preservate (cart/change.js le sostituisce in
        //   blocco, quindi merge esplicito);
        // - best-effort: un errore di rete non blocca il commit;
        // - cartRead: carrello gia' letto da _restoreSessionExtras (una GET
        //   in meno prima del redirect); senza, lo legge qui.
        async _tagSessionExtrasToBike(root, cartRead) {
          var extras = this._sessionExtras || {};
          var ids = Object.keys(extras);
          if (!ids.length) return;
          var bikeVid = String(this.dataset.bikeVariantId);
          try {
            var cart = cartRead;
            if (!cart) {
              var res = await fetch(root + "cart.js", {
                headers: { Accept: "application/json" },
              });
              if (!res.ok) return;
              cart = await res.json();
            }
            for (var i = 0; i < (cart.items || []).length; i++) {
              var it = cart.items[i];
              if (it.properties && it.properties.__bkrm_parent_variant) continue;
              var matches = ids.some(function (id) {
                var x = extras[id];
                return (x.key && x.key === it.key) || String(it.variant_id) === String(id);
              });
              if (!matches) continue;
              var props = {};
              for (var k in it.properties || {}) props[k] = it.properties[k];
              props.__bkrm_parent_variant = bikeVid;
              // Un tentativo in più su risposta non-ok (429/5xx): prima il
              // risultato veniva ignorato e la riga restava senza parent.
              for (var attempt = 0; attempt < 2; attempt++) {
                var resTag = await fetch(root + "cart/change.js", {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                  },
                  body: JSON.stringify({
                    id: it.key,
                    quantity: it.quantity,
                    properties: props,
                  }),
                });
                if (resTag && resTag.ok) break;
              }
            }
          } catch (e) {
            /* best-effort */
          }
        }

        // Edit mode: reconcile the drawer-managed kit/box for THIS bike. Remove
        // the existing kit/box line items for this parent variant, then add the
        // newly selected kit + box (the bike line itself is left untouched).
        async _commitEdit(root) {
          var bikeVid = String(this.dataset.bikeVariantId);
          var cartRes = await fetch(root + "cart.js", {
            headers: { Accept: "application/json" },
          });
          if (!cartRes.ok) throw new Error("cart.js failed: " + cartRes.status);
          var cart = await cartRes.json();
          var stale = (cart.items || []).filter(function (i) {
            return (
              i.properties &&
              i.properties.__bkrm_drawer_select &&
              String(i.properties.__bkrm_parent_variant) === bikeVid
            );
          });
          for (var s = 0; s < stale.length; s++) {
            var chg = await fetch(root + "cart/change.js", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
              },
              body: JSON.stringify({ id: stale[s].key, quantity: 0 }),
            });
            if (!chg.ok) throw new Error("cart/change failed: " + chg.status);
          }
          var addRes = await fetch(root + "cart/add.js", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({
              // buildAddItems senza la riga bici: qui la bici e' gia' in
              // carrello e non va toccata. La pedivella DEVE passare da qui,
              // altrimenti lo sweep degli item __bkrm_drawer_select qui sopra
              // la rimuoverebbe a ogni Edit senza riaggiungerla.
              items: window.BkrmConfigurator.buildAddItems(this.cartState()).slice(1),
            }),
          });
          if (!addRes.ok)
            throw new Error("cart/add (edit) failed: " + addRes.status);
        }

        // ================= Modalità BID (Place a bid) =================
        // Il configuratore serve anche il flusso offerta: stesso pannello
        // full-screen, pannello offerta [data-bid-only] sopra kit/box, CTA
        // "Place bid" -> POST bike-seller invece di /cart/add.js. Attivata da
        // product.js via openBid(); mai in edit-mode (markup assente).

        _initBidMode() {
          this.bidPanel = this.querySelector("[data-bid-offer-panel]");
          if (!this.bidPanel) return;
          this.bidState = { value: 0, expiration: "3" };
          this._bidUpsells = {};
          this._bidOfferOk = false;
          var self = this;
          var input = this.querySelector("[data-bid-offer-input]");
          var range = this.querySelector("[data-bid-offer-range]");
          if (input) {
            // Focus: drop the "4.981" grouping back to plain digits so the
            // user edits a clean number instead of fighting separators/cursor
            // position. Reformatted again on blur (onchange below).
            input.onfocus = function (e) {
              e.target.value = self.bidState.value || "";
            };
            input.onchange = function (e) {
              self.syncBidOffer(parseOfferGroup(e.target.value), "input");
            };
          }
          if (range)
            range.oninput = function (e) { self.syncBidOffer(e.target.value, "range"); };
          var counter = this.querySelector("[data-bid-offer-counter]");
          if (counter)
            counter.onclick = function () { self.syncBidOffer(self._bidAnchors().floor); };
          // Scadenza non piu' selezionabile: resta il "3" di bidState qui
          // sopra, che e' l'unico valore che raggiunge il payload. Il
          // pannello mostra solo il testo (product-configurator.liquid).
          var instant = this.querySelector("[data-bid-instant]");
          if (instant)
            instant.onclick = function () { self.instantBuy(); };
          var recap = this.querySelector("[data-bid-offer-recap]");
          if (recap)
            recap.onclick = function () { self._setStep("bid-offer"); };
        }

        _bidPriceMajor() {
          return parseInt(this.dataset.bikePrice || "0", 10) / 100;
        }

        _bidConstraints() {
          return window.BkrmBidOffer.constraintsFor({
            price: this._bidPriceMajor(),
            comparePrice: parseInt(this.dataset.comparePrice || "0", 10) / 100,
            isBkrm: this.dataset.isBkrm === "true",
            isNew: this.dataset.isNew === "true",
          });
        }

        _bidAnchors() {
          return window.BkrmBidOffer.anchors(this._bidPriceMajor(), this._bidConstraints());
        }

        // Regime fiscale del kit: in modalità bid le righe servizio devono
        // puntare alla variante Standard/Reduced/Margin corretta per la bici
        // corrente (stessa regola del vecchio dialog: taxable + soglia €2000·fx).
        // Il flusso add-to-cart resta sulla variante default renderizzata dal
        // Liquid: _restoreDefaultServiceVariants() la ripristina a ogni open().
        _serviceVariantMap() {
          if (this._svcMap !== undefined) return this._svcMap;
          var el = this.querySelector("[data-cfg-service-variant-map]");
          try {
            this._svcMap = el ? JSON.parse(el.textContent) : null;
          } catch (e) {
            this._svcMap = null;
          }
          return this._svcMap;
        }

        _serviceRows() {
          return this.querySelectorAll(
            '.cd-config-row-main[data-cd-config-group="service-pack"]'
          );
        }

        _applyBidServiceVariants() {
          var map = this._serviceVariantMap();
          if (!map) return;
          var priceCents = parseInt(this.dataset.bikePrice || "0", 10);
          var fx = (window.Shopify && window.Shopify.exchange_rate) || 1;
          var limit = 2000 * 100 * fx;
          var taxable = this.dataset.taxable === "true";
          var title, altTitle;
          if (!taxable) {
            altTitle = "Margin";
            title = priceCents > limit ? "Margin" : "Reduced_margin";
          } else {
            altTitle = "Standard";
            title = priceCents > limit ? "Standard" : "Reduced";
          }
          this._serviceRows().forEach(function (btn) {
            var entry = map[btn.dataset.handle];
            if (!entry) return;
            if (!btn.dataset.defaultVariantId) {
              btn.dataset.defaultVariantId = btn.dataset.variantId;
              var pe0 = btn.querySelector(".cd-config-row-price");
              if (pe0) {
                btn.dataset.defaultPrice = pe0.dataset.price || "0";
                btn.dataset.defaultPriceText = pe0.textContent.trim();
                btn.dataset.defaultComparePrice = btn.dataset.comparePrice || "0";
              }
            }
            var wanted = entry.multi ? title : altTitle;
            var v = entry.variants[wanted] || entry.variants[altTitle];
            if (!v) return;
            btn.dataset.variantId = String(v.id);
            btn.dataset.comparePrice = String(v.compareAt || 0);
            var pe = btn.querySelector(".cd-config-row-price");
            if (pe) {
              pe.dataset.price = String(v.price);
              if (v.price > 0) pe.textContent = v.priceFormatted;
              pe.classList.toggle("cd-config-row-price--free", v.price === 0);
            }
          });
        }

        _restoreDefaultServiceVariants() {
          this._serviceRows().forEach(function (btn) {
            if (!btn.dataset.defaultVariantId) return;
            btn.dataset.variantId = btn.dataset.defaultVariantId;
            btn.dataset.comparePrice = btn.dataset.defaultComparePrice || "0";
            var pe = btn.querySelector(".cd-config-row-price");
            if (pe) {
              pe.dataset.price = btn.dataset.defaultPrice || "0";
              if (btn.dataset.defaultPriceText) pe.textContent = btn.dataset.defaultPriceText;
              pe.classList.toggle(
                "cd-config-row-price--free",
                (btn.dataset.defaultPrice || "0") === "0"
              );
            }
          });
        }

        // ---- Named step machine: bid-offer(bid only) -> kit -> box -> upsell -> review ----
        // La personalizzazione (pedivella, pacco pignoni, guarnitura) e' un
        // extra legato al kit: la aprono solo i kit in data-custom-kits
        // (oggi Plus e Ride like a Pro).
        //
        // Il controllo NON puo' stare in Liquid: il kit si sceglie a runtime
        // dentro il drawer, quindi al render non si sa ancora. Si legge
        // l'handle dalla riga selezionata, che cart-drawer-configurator
        // scrive in data-handle.
        //
        // Senza kit scelto il risultato e' false: il blocco resta nascosto
        // finche' non si sa che e' dovuto.
        _isCustomKitSelected() {
          var ammessi = this._customAllowedKits();
          if (!ammessi.length) return false;
          var riga = this.querySelector(
            '.cd-config-row--selected .cd-config-row-main[data-cd-config-group="service-pack"]',
          );
          if (!riga) return false;
          return ammessi.indexOf(riga.dataset.handle) !== -1;
        }

        // Riga del kit per handle, con il suo prezzo in centesimi.
        _kitRow(handle) {
          return this.querySelector(
            '.cd-config-row-main[data-cd-config-group="service-pack"][data-handle="' +
              handle +
              '"]',
          );
        }

        _kitCents(row) {
          if (!row) return 0;
          var p = row.querySelector(".cd-config-row-price");
          if (!p) return 0;
          return parsePriceCents(p.dataset.price ?? p.textContent);
        }

        // Upgrade al primo kit ammesso (oggi Plus). Cambia la selezione
        // dello step 1 invece di aggiungere una riga a parte: il kit e'
        // uno solo per bici, e cosi' il recap resta coerente con quello
        // che si vede tornando indietro.
        //
        // Non si simula un click sulla riga del kit: passerebbe da onClick,
        // che azzera _customUpgraded trattandolo come scelta manuale.
        _toggleCustomUpgrade(attivo) {
          var target = attivo
            ? this._kitRow(this._customAllowedKits()[0])
            : this._kitRow(this._customUpgradeFrom || "base");
          if (!target) return;
          this._customUpgraded = !!attivo;
          this.state.kitVariantId = target.dataset.variantId;
          this.markSelected("service-pack", target);
          this._syncCustomBlock();
          this.syncProceed();
          this.updateSummary();
        }

        _customAllowedKits() {
          return String(this.dataset.customKits || "")
            .split(",")
            .map(function (h) {
              return h.trim();
            })
            .filter(Boolean);
        }

        // Stato completo di cio' che finisce in carrello, in UN posto solo.
        //
        // Nasce da un bug vero: proceed() e il gate di login ricostruivano
        // l'oggetto campo per campo e nessuno dei due passava la pedivella,
        // che si sceglieva, compariva nel recap col suo prezzo... e spariva
        // al momento dell'aggiunta. I test non l'avevano preso perche'
        // costruivano l'oggetto a mano invece di passare da qui.
        cartState() {
          return {
            bikeVariantId: this.dataset.bikeVariantId,
            kitVariantId: this.state.kitVariantId,
            boxVariantId: this.state.boxVariantId,
            addons: Object.assign({}, this.state.addons),
          };
        }

        _setAddonLabel(btn, testo) {
          if (!btn || !testo) return;
          var span = btn.querySelector("span");
          if (span) span.textContent = testo;
          else btn.textContent = testo;
        }

        // Card di un addon per chiave ('crank', 'cassette', 'crankset', ...).
        // La pellicola puo' avere due card con la stessa chiave (MTB senza
        // tag front/full): conta quella che contiene la variante scelta.
        _addonCard(key, variantId) {
          var cards = this.querySelectorAll('[data-cfg-addon="' + key + '"]');
          if (variantId && cards.length > 1) {
            for (var i = 0; i < cards.length; i++) {
              if (cards[i].querySelector('option[value="' + variantId + '"]')) return cards[i];
            }
          }
          if (cards.length > 1 && this._addonChoices && this._addonChoices[key]) {
            var scelta = this._addonChoices[key].id;
            for (var j = 0; j < cards.length; j++) {
              if (cards[j].querySelector('option[value="' + scelta + '"]')) return cards[j];
            }
          }
          return cards[0] || null;
        }

        // Toglie la scelta di UN addon e riporta la sua card a "Add".
        // Non tocca l'upgrade del kit: togliere la cassetta non deve far
        // sparire la checkbox con cui si e' passati a Plus, altrimenti non
        // si potrebbe piu' tornare indietro.
        _clearAddon(key) {
          if (this.state && this.state.addons) delete this.state.addons[key];
          if (this._addonChoices) delete this._addonChoices[key];
          // Tutte le card con questa chiave (la pellicola puo' averne due).
          var self = this;
          this.querySelectorAll('[data-cfg-addon="' + key + '"] [data-cfg-addon-add]').forEach(function (add) {
            add.setAttribute("aria-pressed", "false");
            self._setAddonLabel(add, add.dataset.tAdd);
          });
        }

        // Fissa la scelta di un addon da un'<option> della sua select. La
        // scelta vive in state.addons (per il carrello) e in _addonChoices
        // (titolo e prezzo per updateSummary), non nel DOM: la card non ha
        // una riga "selezionata" da interrogare come kit e box.
        _chooseAddon(key, opt) {
          // Stessa chiave su due card (pellicola MTB): sceglierne una
          // libera l'altra, cosi' in carrello ne finisce una sola.
          if (this.state.addons[key] && this.state.addons[key] !== opt.value) {
            this._clearAddon(key);
          }
          this.state.addons[key] = opt.value;
          var titolo = opt.dataset.title || opt.textContent.trim();
          // Pellicola MTB senza tag (due kit, una chiave): nel pannello e
          // nello scontrino va detto QUALE kit, non solo la finitura. Il
          // nome breve e' il titolo della card senza "Covered Protection –".
          var filmCards = key === "film" ? this.querySelectorAll('[data-cfg-addon="film"]') : [];
          if (filmCards.length > 1) {
            var fc = this._addonCard(key, opt.value);
            var ft = fc && fc.querySelector(".card-drawer-upsell-title");
            var breve = ft ? ft.textContent.trim().split(" – ").slice(1).join(" – ") : "";
            if (breve) titolo = breve + " · " + titolo;
            // La card lasciata torna sul segnaposto, non su una finitura
            // che non e' piu' scelta.
            for (var fi = 0; fi < filmCards.length; fi++) {
              if (filmCards[fi] === fc) continue;
              var fsel = filmCards[fi].querySelector("[data-cfg-addon-select]");
              if (fsel) fsel.value = "";
            }
          }
          this._addonChoices[key] = {
            id: opt.value,
            title: titolo,
            // Variante unica ("Default Title"): niente da mostrare accanto al
            // nome nel pannello e nello scontrino, ripeterebbe il prodotto.
            plain: opt.dataset.plain === "true",
            cents: parseInt(opt.dataset.price || "0", 10),
          };
          var card = this._addonCard(key, opt.value);
          var add = card && card.querySelector("[data-cfg-addon-add]");
          if (add) {
            add.setAttribute("aria-pressed", "true");
            this._setAddonLabel(add, add.dataset.tAdded);
          }
        }

        _toggleAddon(btn) {
          var card = btn.closest("[data-cfg-addon]");
          if (!card) return;
          var key = card.dataset.cfgAddon;
          var select = card.querySelector("[data-cfg-addon-select]");
          if (!select) return;
          // Il blocco delle card col kit Base era solo CSS (pointer-events):
          // da tastiera (Tab + Invio) un upgrade entrava comunque in
          // carrello. Qui il controllo vero, per ogni lavoro d'officina.
          if (
            CUSTOM_ADDON_KEYS.indexOf(key) !== -1 &&
            !this.state.addons[key] &&
            !this._isCustomKitSelected()
          ) {
            return;
          }
          // Rimuovi solo se la scelta e' di QUESTA card: con due card sulla
          // stessa chiave (pellicola MTB) "Aggiungi" sull'altra sostituisce.
          if (
            this.state.addons[key] &&
            select.querySelector('option[value="' + this.state.addons[key] + '"]')
          ) {
            this._clearAddon(key);
            this.updateSummary();
            return;
          }
          var opt = select.options[select.selectedIndex];
          // Card si'/no (componenti): la variante l'ha gia' decisa il Liquid
          // e la select e' nascosta. resetSelection la svuota come le
          // altre, quindi la si ritrova per valore invece che per indice.
          if ((!opt || !opt.value) && card.dataset.cfgAddonAuto) {
            opt = select.querySelector(
              'option[value="' + card.dataset.cfgAddonAuto + '"]',
            );
            if (opt) select.value = opt.value;
          }
          if (!opt || !opt.value) {
            // Nessuna variante scelta: si porta l'attenzione sulla select
            // invece di aggiungere in silenzio una variante arbitraria.
            select.focus();
            return;
          }
          this._chooseAddon(key, opt);
          this.updateSummary();
        }

        // Decide cosa si vede dentro lo step "Pedals & accessories":
        //
        //   kit Plus  -> blocco visibile, card usabili, niente upgrade
        //   kit Base  -> blocco visibile ma card bloccate, con la riga di
        //                upgrade: le card si vedono perche' sono il motivo
        //                per cui uno fa l'upgrade
        //   altri kit -> blocco nascosto
        //
        // Scarta anche gli addon gia' scelti quando il nuovo kit non li
        // prevede: senza quella pulizia sopravvivrebbero al cambio e
        // finirebbero comunque in carrello via buildAddItems.
        _syncCustomBlock() {
          var blocco = this.querySelector("[data-cfg-custom-block]");
          if (!blocco) return;
          var ammesso = this._isCustomKitSelected();
          var kitCorrente = this.querySelector(
            '.cd-config-row--selected .cd-config-row-main[data-cd-config-group="service-pack"]',
          );
          var handle = kitCorrente ? kitCorrente.dataset.handle : "";
          var upgradabile = handle === (this._customUpgradeFrom || "base");

          blocco.hidden = !(ammesso || upgradabile);

          // Due sezioni (trasmissione, componenti), ognuna con la sua
          // griglia: il blocco si applica a tutte.
          this.querySelectorAll("[data-cfg-custom-cards]").forEach(function (cards) {
            cards.dataset.customLocked = ammesso ? "false" : "true";
          });

          var riga = this.querySelector("[data-cfg-custom-upgrade]");
          if (riga) {
            // Visibile col kit Base, e anche dopo l'upgrade: sparendo una
            // volta spuntata non si potrebbe piu' tornare indietro.
            riga.hidden = !(upgradabile || this._customUpgraded);
            var box = riga.querySelector("[data-cfg-custom-upgrade-box]");
            if (box) box.checked = !!this._customUpgraded;
            var prezzo = riga.querySelector("[data-cfg-custom-upgrade-price]");
            if (prezzo) {
              var delta =
                this._kitCents(this._kitRow(this._customAllowedKits()[0])) -
                this._kitCents(this._kitRow(this._customUpgradeFrom || "base"));
              prezzo.textContent = delta > 0 ? "+" + formatCents(delta) : "";
            }
          }

          if (!ammesso && this.state) {
            var self = this;
            CUSTOM_ADDON_KEYS.forEach(function (key) {
              self._clearAddon(key);
            });
            this._customUpgraded = false;
          }
          this.updateSummary();
        }

        // Step presenti per QUESTA bici, scritti dal Liquid in data-cfg-steps
        // (cfg_steps): uno step senza prodotti non esiste, e il contatore
        // "Passo N di T" deve contare solo quelli rimasti. Senza attributo
        // si guardano i pannelli presenti nel DOM: mai offrire uno step che
        // a schermo non avrebbe niente.
        _availableSteps() {
          var raw = this.dataset.cfgSteps;
          if (!raw) {
            return Array.prototype.map.call(
              this.querySelectorAll("[data-cfg-step-panel]"),
              function (p) {
                return p.dataset.cfgStepPanel;
              },
            );
          }
          return raw
            .split(",")
            .map(function (k) {
              return k.trim();
            })
            .filter(Boolean);
        }

        _steps() {
          return window.BkrmConfigurator.stepList(
            this.dataset.mode,
            this._availableSteps(),
          );
        }

        // "Scelta richiesta" sugli step che non si possono attraversare a
        // vuoto (kit, pedali, box), "Opzionale" sugli altri, nessuna chip su
        // offerta e review. Le due etichette sono le sole ammesse (Umberto,
        // thread del 09/10/2026: "usiamo sempre la stessa nomenclatura").
        _stepChip(stepKey) {
          if (stepKey === "kit" || stepKey === "pedals" || stepKey === "box")
            return "required";
          if (
            stepKey === "custom" ||
            stepKey === "accessories" ||
            stepKey === "upgrade"
          )
            return "optional";
          return "";
        }

        // Barra a segmenti: fatti e corrente pieni, futuri grigi. Su desktop
        // la label sotto ogni segmento diventa un link solo per gli step gia'
        // raggiunti (_maxStepIdx): saltare in avanti oltre lo step pedali
        // aggirerebbe la sua validazione.
        _renderProgress(stepKey) {
          var list = this._steps();
          var idx = list.indexOf(stepKey);
          if (idx > (this._maxStepIdx || 0)) this._maxStepIdx = idx;
          var max = this._maxStepIdx || 0;
          this.querySelectorAll("[data-cfg-progress-step]").forEach(function (li) {
            var key = li.dataset.cfgProgressStep;
            var i = list.indexOf(key);
            li.hidden = i === -1;
            li.classList.toggle("is-done", i !== -1 && i < idx);
            li.classList.toggle("is-current", i === idx);
            var jump = li.querySelector("[data-cfg-progress-jump]");
            if (jump) {
              jump.disabled = i === -1 || i === idx || i > max;
              if (i === idx) jump.setAttribute("aria-current", "step");
              else jump.removeAttribute("aria-current");
            }
          });
        }

        _setStep(stepKey) {
          // Uno step che non esiste lascia il pannello in uno stato senza
          // nome: la matrice CSS nasconde i pannelli elencando i data-step
          // NOTI, quindi con un valore sconosciuto non nasconderebbe niente.
          // Si valida contro la lista completa del bid (che contiene anche
          // 'bid-offer', legittimo prima che dataset.mode diventi "bid") e
          // poi contro gli step presenti per questa bici.
          var noti = window.BkrmConfigurator.stepList(
            "bid",
            this._availableSteps(),
          );
          if (noti.indexOf(stepKey) === -1) return;
          this.dataset.step = stepKey;
          if (stepKey === "review") this._renderReceipt();
          // Ogni cambio di step passa da qui: è il punto unico dove misurare
          // l'abbandono dentro il wizard.
          var stepMode = this.dataset.mode === "bid" ? "bid" : "standard";
          var steps = this._steps();
          track("configurator step viewed", {
            step_name: stepKey,
            step_index: steps.indexOf(stepKey) + 1,
            step_total: steps.length,
            mode: stepMode,
            // L'edit dal cart drawer percorre lo stesso wizard: senza il
            // flag i suoi step inquinerebbero l'abbandono del funnel PDP.
            edit_mode: this.dataset.editMode === "true",
          });
          if (stepKey === "bid-offer") {
            // Tornare all'offerta esce dal percorso instant-buy (parità con
            // la vecchia modal: il Back da step kit ripristinava Place bid).
            this._bidInstant = false;
          }
          // L'errore inline (es. pedali mancanti) appartiene allo step in
          // cui e' nato: cambiando step non deve seguire l'utente.
          this.hideError();
          if (this.proceedBtn) {
            var label;
            if (this.dataset.mode === "bid") {
              if (stepKey === "review") {
                label = this._bidInstant
                  ? this.dataset.tGoCheckout || this.dataset.tPlaceBid
                  : this.dataset.tPlaceBid || this._proceedLabel;
              } else if (stepKey === "bid-offer") {
                label = this.dataset.tNext || this._proceedLabel;
              } else {
                // Dopo l'offerta gli step sono quelli dell'acquisto, con lo
                // stesso "Continua" (Davide, 21/10/2026).
                label = this._proceedLabel;
              }
            } else {
              label = this._proceedLabel;
            }
            this.proceedBtn.textContent = label;
          }
          var indicator = this.querySelector("[data-cfg-step-indicator]");
          if (indicator) {
            var idx = steps.indexOf(stepKey);
            if (idx !== -1) {
              indicator.textContent = (indicator.dataset.tStepLabel || "Step [n] of [total]")
                .replace("[n]", String(idx + 1))
                .replace("[total]", String(steps.length));
            }
          }
          // Titolo per-step: il titolo generico "Completa la tua bici" non
          // c'e' piu' (handoff, sezione 3). "bid-offer" riusa il titolo bid
          // di openBid(): senza la entry, tornando all'offerta col back il
          // titolo restava quello dello step kit.
          var titleEl = this.querySelector(".bike-configurator__title");
          if (titleEl) {
            var titleMap = {
              "bid-offer": this.dataset.tBidTitle,
              kit: this.dataset.tTitleKit,
              custom: this.dataset.tTitleCustom,
              pedals: this.dataset.tTitlePedals,
              accessories: this.dataset.tTitleAccessories,
              upgrade: this.dataset.tTitleUpgrade,
              box: this.dataset.tTitleBox,
              review: this.dataset.tTitleReview,
            };
            var stepTitle = titleMap[stepKey];
            if (stepTitle) {
              if (!this._cfgTitleLabel) this._cfgTitleLabel = titleEl.textContent;
              titleEl.textContent = stepTitle;
            }
          }
          var chip = this._stepChip(stepKey);
          var requiredChip = this.querySelector("[data-cfg-step-required]");
          if (requiredChip) requiredChip.hidden = chip !== "required";
          var optionalChip = this.querySelector("[data-cfg-step-optional]");
          if (optionalChip) optionalChip.hidden = chip !== "optional";
          this._renderProgress(stepKey);
          // Ogni step entra col breakdown collassato (Figma). Nel bid resta
          // aperto solo sullo step dell'offerta, come lo apre openBid():
          // offerta e Rider Protection su due righe (feedback Alberto). Dopo,
          // gli step sono quelli dell'acquisto e il pannello aperto (343 px)
          // copriva meta' dello step e lo scontrino del riepilogo (Davide,
          // 21/10/2026: parte di personalizzazione uguale all'acquisto).
          if (this.dataset.mode === "bid" && stepKey === "bid-offer") {
            var bidSum = this.querySelector(".bike-configurator__summary");
            var bidSumToggle = this.querySelector(".bike-configurator__summary-total-toggle");
            if (bidSum) bidSum.classList.add("bike-configurator__summary--expanded");
            if (bidSumToggle) bidSumToggle.setAttribute("aria-expanded", "true");
          } else {
            this._collapseSummary();
          }
          this.resetScroll();
          this.syncProceed();
        }

        // Step pedali: chi va avanti senza pedali deve dirlo. Nessuna bici li
        // include, e il drop-off misurato sullo step era di chi non lo
        // sapeva (handoff sezione 5). Prima c'era la card "Nessun pedale";
        // dal feedback di Alberto del 09/10/2026 e' una conferma su Continua.
        // Fuori dal wizard da PDP (edit dal carrello) non blocca: quella bici
        // e' gia' passata di qui.
        _pedalsChoiceOk() {
          if (this.dataset.editMode === "true") return true;
          if (this._noPedals) return true;
          var panel = this.querySelector('[data-cfg-step-panel="pedals"]');
          if (!panel) return true;
          return !!panel.querySelector("quick-view.is-upsell-added");
        }

        // Avanzamento dagli step intermedi, comune a buy e bid. Restituisce
        // false se lo step corrente si ferma (conferma pedali aperta).
        _advanceFrom(step) {
          if (this.proceedBtn && this.proceedBtn.disabled) return false;
          if (step === "pedals" && !this._pedalsChoiceOk()) {
            track("configurator pedals confirm shown", {
              variant_id: String(this.dataset.bikeVariantId),
              mode: this.dataset.mode === "bid" ? "bid" : "standard",
            });
            // Senza il markup del dialog (pagina vecchia in cache) non si
            // blocca il cliente: si va avanti come prima del gate.
            if (this._showPedalsConfirm()) return false;
          }
          if (step === "pedals") {
            track("configurator pedals choice", {
              variant_id: String(this.dataset.bikeVariantId),
              choice: this._pedalsChoiceOk() && !this._noPedals ? "pedal" : "none",
            });
          }
          this._setStep(
            window.BkrmConfigurator.nextStep(
              this.dataset.mode,
              step,
              this._availableSteps(),
            ),
          );
          return true;
        }

        async _onBidProceed() {
          var step = this.dataset.step;
          if (step === "bid-offer") {
            if (this._bidOfferOk !== true) return;
            if (!this.dataset.customerId && !this._bidInstant) {
              this._openBidLogin();
              return;
            }
            track("bid offer set", {
              variant_id: String(this.dataset.bikeVariantId),
              offer: this.bidState.value,
              list_price: this._bidPriceMajor(),
              currency: this.dataset.currency,
            });
            this._setStep("kit");
            return;
          }
          if (step !== "review") {
            this._advanceFrom(step);
            return;
          }
          // step === "review"
          if (!window.BkrmConfigurator.isConfigComplete(this.state)) return;
          if (this._bidInstant) {
            if (!this.dataset.customerId) {
              this._openBidLogin();
              return;
            }
            await this._instantCheckout();
            return;
          }
          await this._placeBid();
        }

        openBid(startValue) {
          if (!this.bidPanel) return;
          this._openingBid = true;
          // Stessa derivazione della var `resumed` più sotto, ma calcolata
          // PRIMA di open(): è lì che l'evento "configurator opened" viene
          // emesso e deve distinguere il resume post-login dal click utente.
          var resumeStart = Number(startValue);
          this._openingBidResumed = isFinite(resumeStart) && resumeStart > 0;
          this.dataset.mode = "bid";
          this.open();
          this._openingBid = false;
          this._openingBidResumed = false;
          this._bidUpsells = {};
          this._applyBidServiceVariants();
          if (this.proceedBtn && this.dataset.tPlaceBid)
            this.proceedBtn.textContent = this.dataset.tPlaceBid;
          var titleEl = this.querySelector(".bike-configurator__title");
          if (titleEl && this.dataset.tBidTitle) {
            if (!this._cfgTitleLabel) this._cfgTitleLabel = titleEl.textContent;
            titleEl.textContent = this.dataset.tBidTitle;
          }
          // Breakdown APERTO di default nel bid: offerta bici e Rider
          // Protection su due righe separate, così il totale non confonde
          // (feedback Alberto — al posto della nota sotto il totale).
          // open() lo collassa sempre: riaprilo dopo.
          var bidSummary = this.querySelector(".bike-configurator__summary");
          var bidSummaryToggle = this.querySelector(
            ".bike-configurator__summary-total-toggle"
          );
          if (bidSummary)
            bidSummary.classList.add("bike-configurator__summary--expanded");
          if (bidSummaryToggle) bidSummaryToggle.setAttribute("aria-expanded", "true");
          // Bound slider/scale/instant dagli anchor della matrice (dopo open():
          // i dataset possono essere cambiati da onVariantChange).
          var priceMajor = this._bidPriceMajor();
          var a = this._bidAnchors();
          var range = this.querySelector("[data-bid-offer-range]");
          if (range) {
            range.min = a.floor;
            range.max = priceMajor;
            range.step = 1;
          }
          var minL = this.querySelector("[data-bid-range-min]");
          var maxL = this.querySelector("[data-bid-range-max]");
          if (minL) minL.textContent = formatCents(a.floor * 100);
          if (maxL) maxL.textContent = formatCents(priceMajor * 100);
          var instantPrice = this.querySelector("[data-bid-instant-price]");
          if (instantPrice) instantPrice.textContent = formatCents(priceMajor * 100);
          // Default = prezzo suggerito (anchor `max`): e' l'unico numero che
          // le regole pubblicano, quindi e' quello che il pannello propone.
          // Prima partiva dall'anchor `min` ("better offer"), che pero' ora
          // vale 0 e coinciderebbe col prezzo pieno.
          // startValue = resume post-login (?value=).
          var c = this._bidConstraints() || {};
          var start = Number(startValue);
          var resumed = isFinite(start) && start > 0;
          if (!resumed)
            start = Math.round(priceMajor * (1 - (typeof c.max === "number" ? c.max : 0.1)));
          this._bidInstant = false;
          this.syncBidOffer(start);
          // Resume post-login (?value=): l'offerta era già stata scelta →
          // dritti allo step kit/box, come la vecchia modal (goToStep(1)).
          this._setStep(resumed ? "kit" : "bid-offer");
          track("bid form opened", {
            variant_id: String(this.dataset.bikeVariantId),
            list_price: priceMajor,
            currency: this.dataset.currency,
            market: this.dataset.market,
          });
        }

        // Unico punto d'aggiornamento dell'offerta: sincronizza input/slider,
        // valida (chip probabilita' / controproposta), aggiorna recap e CTA.
        syncBidOffer(v, source) {
          var priceMajor = this._bidPriceMajor();
          var value = Number(v);
          if (!isFinite(value) || value < 0) value = 0;
          if (value > priceMajor) value = priceMajor;
          var input = this.querySelector("[data-bid-offer-input]");
          var range = this.querySelector("[data-bid-offer-range]");
          // Grouped display (e.g. "4.981") whenever the field isn't the one
          // currently being edited — same separators as the og_price/slider
          // labels in this same panel, read off the shop's money format by
          // formatOfferGroup. While the user is actively typing, onfocus has
          // already stripped the grouping, and onchange only fires on blur,
          // so this never fights an in-progress keystroke.
          if (input) input.value = value ? formatOfferGroup(value) : "";
          if (range && source !== "range") {
            var a = this._bidAnchors();
            range.value = Math.min(Math.max(value, a.floor), priceMajor);
          }
          this.bidState.value = value;
          var recapValue = this.querySelector("[data-bid-recap-value]");
          if (recapValue) recapValue.textContent = formatCents(Math.round(value * 100));
          this._applyBidValidation(value);
          this.updateSummary();
          this.syncProceed();
        }

        _applyBidValidation(value) {
          var r = window.BkrmBidOffer.evaluate(value, this._bidPriceMajor(), this._bidConstraints());
          var panel = this.bidPanel;
          var feedback = this.querySelector("[data-bid-offer-feedback]");
          var chip = this.querySelector("[data-bid-offer-chip]");
          var chipLabel = this.querySelector("[data-bid-chip-label]");
          var msg = this.querySelector("[data-bid-offer-msg]");
          var counter = this.querySelector("[data-bid-offer-counter]");
          if (!panel || !feedback || !chip) return;

          chip.classList.remove("acc-high", "acc-good", "acc-low");
          feedback.classList.toggle("hidden", r.state !== "ok");
          if (counter) counter.classList.toggle("hidden", r.state !== "counter");

          if (r.state === "ok") {
            chip.classList.add("acc-" + r.level);
            var labels = {
              high: panel.dataset.tAcceptHigh,
              good: panel.dataset.tAcceptGood,
              low: panel.dataset.tAcceptLow,
            };
            if (chipLabel) chipLabel.textContent = labels[r.level] || "";
            var discounted =
              parseInt(this.dataset.comparePrice || "0", 10) >
              parseInt(this.dataset.bikePrice || "0", 10);
            if (msg)
              msg.textContent = (discounted ? panel.dataset.tMsgDiscounted : panel.dataset.tMsgFull || "")
                .replace("[percent]", r.acceptDiscountPct);
          } else if (r.state === "counter" && counter) {
            counter.textContent = (panel.dataset.tCounter || "").replace(
              "[price]",
              formatCents(r.floor * 100)
            );
          }
          this._bidOfferOk = r.state === "ok";
        }

        // Toggle upsell nel payload del bid (nessun carrello): riusa il recap
        // extras (_sessionExtras/renderExtras) per le righe nel totale.
        // Titolo di una card per recap e review: con una variante scelta ci
        // va anche lei ("MET Trenta 3K Carbon 2 - White · M 56cm-58cm"),
        // altrimenti nel recap due taglie dello stesso casco sarebbero
        // indistinguibili (handoff, sezione 6).
        _upsellCardTitle(card) {
          var title = card.dataset.upsellTitle || "";
          var label = card.dataset.upsellVariantLabel;
          return label ? title + " · " + label : title;
        }

        _isCardAdded(card) {
          return Object.prototype.hasOwnProperty.call(
            this._sessionExtras || {},
            card.dataset.upsellVariantId,
          );
        }

        // Punto unico del click su una card prodotto, buy e bid.
        //  - Card con selettore e nessuna variante scelta: non aggiunge, porta
        //    il focus sulla select ("Aggiungi" disabilitato finche' non c'e'
        //    una taglia).
        //  - Pedali: multi-selezione come gli altri step (feedback Alberto,
        //    09/10/2026: "voglio lasciare la possibilita' di selezionare anche
        //    piu' pedali"). Scegliere un pedale annulla un "continua senza
        //    pedali" confermato prima.
        //  - Focus: se il comando e' arrivato da tastiera su Aggiungi/Rimuovi,
        //    quel bottone sparisce col toggle (ne e' visibile uno alla volta):
        //    il focus passa al gemello invece di cadere sul body.
        async _toggleUpsellCard(card, force) {
          if (!card.dataset.upsellVariantId) {
            var picker = card.querySelector("[data-cfg-upsell-variant]");
            if (picker) picker.focus();
            return;
          }
          var active = document.activeElement;
          var hadButtonFocus =
            !!active &&
            card.contains(active) &&
            !!active.closest(".cd-upsell-add-btn, .cd-upsell-remove-btn");
          var adding = !this._isCardAdded(card);
          if (this.dataset.mode === "bid") this._toggleBidUpsell(card, force);
          else await this._toggleCartUpsell(card, force);
          if (card.closest('[data-cfg-step-panel="pedals"]')) {
            if (adding && this._isCardAdded(card)) this._noPedals = false;
            this._updateReviewUpsellSlots();
          }
          if (hadButtonFocus) {
            var twin = card.querySelector(
              this._isCardAdded(card) ? ".cd-upsell-remove-btn" : ".cd-upsell-add-btn",
            );
            if (twin) twin.focus();
          }
        }

        // La card assume la variante scelta nella select: id, prezzo ed
        // etichetta viaggiano sui suoi data-*, che sono quello che leggono
        // add, recap e review. Senza scelta torna "in attesa" e "Aggiungi"
        // e' aria-disabled (resta nel Tab: un bottone disabled sparirebbe
        // dall'ordine di tabulazione senza spiegare perche').
        _applyCardVariant(card, select) {
          var opt = select.options[select.selectedIndex];
          var priceEl = card.querySelector("[data-cfg-upsell-price]");
          var addBtn = card.querySelector(".cd-upsell-add-btn");
          if (opt && opt.value) {
            card.dataset.upsellVariantId = opt.value;
            card.dataset.upsellPrice = opt.dataset.price || card.dataset.upsellPrice;
            card.dataset.upsellVariantLabel = opt.dataset.label || "";
            if (priceEl && opt.dataset.priceFormatted)
              priceEl.textContent = opt.dataset.priceFormatted;
            card.classList.remove("is-variant-pending");
            if (addBtn) addBtn.removeAttribute("aria-disabled");
          } else {
            card.dataset.upsellVariantId = "";
            delete card.dataset.upsellVariantLabel;
            card.classList.add("is-variant-pending");
            if (addBtn) addBtn.setAttribute("aria-disabled", "true");
          }
        }

        // Ogni apertura riparte con le select vuote, tranne sulle card gia'
        // aggiunte (ci pensa poi _seedCartUpsells a riallinearle al
        // carrello).
        _resetVariantPickers() {
          var self = this;
          this.querySelectorAll("[data-cfg-upsell-variant]").forEach(function (select) {
            var card = select.closest("quick-view");
            if (!card || self._isCardAdded(card)) return;
            select.value = "";
            select.disabled = false;
            self._applyCardVariant(card, select);
          });
        }

        // Conferma "senza pedali" (feedback Alberto, 09/10/2026): sostituisce
        // la card "Nessun pedale". Il dialog vive nel markup
        // ([data-cfg-pedals-confirm]); si apre premendo Continua sullo step
        // pedali senza pedali scelti. Il focus va sull'azione primaria
        // ("Scegli i pedali") e torna su Continua alla chiusura.
        _pedalsConfirmEl() {
          return this.querySelector("[data-cfg-pedals-confirm]");
        }

        _showPedalsConfirm() {
          var dlg = this._pedalsConfirmEl();
          if (!dlg) return false;
          dlg.removeAttribute("hidden");
          var primary = dlg.querySelector(".bike-configurator__alert-continue");
          if (primary) primary.focus();
          return true;
        }

        _hidePedalsConfirm() {
          var dlg = this._pedalsConfirmEl();
          if (!dlg || dlg.hidden) return;
          dlg.setAttribute("hidden", "");
          if (this.proceedBtn) this.proceedBtn.focus();
        }

        // "Continua senza pedali": la scelta conta come fatta (review:
        // "Nessun pedale") e si avanza.
        _confirmNoPedals() {
          this._noPedals = true;
          this._hidePedalsConfirm();
          this._updateReviewUpsellSlots();
          this._advanceFrom("pedals");
        }

        _applyCustomFilter(kind) {
          this.querySelectorAll("[data-cfg-custom-filter]").forEach(function (b) {
            b.setAttribute(
              "aria-pressed",
              b.dataset.cfgCustomFilter === kind ? "true" : "false",
            );
          });
          this.querySelectorAll("[data-cfg-custom-group]").forEach(function (g) {
            g.hidden = kind !== "all" && g.dataset.cfgCustomGroup !== kind;
          });
        }

        // root: lo step delle chip toccate; senza (apertura del drawer) vale
        // per tutti gli step a sezioni.
        _applyAccFilter(kind, root) {
          var scope = root || this;
          scope.querySelectorAll("[data-cfg-acc-filter]").forEach(function (b) {
            b.setAttribute("aria-pressed", b.dataset.cfgAccFilter === kind ? "true" : "false");
          });
          scope.querySelectorAll("[data-cfg-acc-section]").forEach(function (s) {
            s.hidden = kind !== "all" && s.dataset.cfgAccSection !== kind;
          });
        }

        _applyPedalFilter(kind) {
          this.querySelectorAll("[data-cfg-pedal-filter]").forEach(function (b) {
            b.setAttribute(
              "aria-pressed",
              b.dataset.cfgPedalFilter === kind ? "true" : "false",
            );
          });
          this.querySelectorAll("[data-cfg-pedal-kind]").forEach(function (item) {
            item.hidden = kind !== "all" && item.dataset.cfgPedalKind !== kind;
          });
        }

        // X del riepilogo su un prodotto: stesso percorso del click sulla
        // card, cosi' carrello, payload bid e stato della card restano
        // allineati. Se la card non c'e' (prodotto aggiunto da un altro
        // flusso) si toglie la riga direttamente.
        async _removeExtra(variantId) {
          if (!variantId) return;
          var card = this.querySelector(
            'quick-view[data-upsell-variant-id="' + variantId + '"]',
          );
          if (card && this._isCardAdded(card)) {
            await this._toggleUpsellCard(card, true);
            return;
          }
          var extras = this._sessionExtras || {};
          var x = extras[variantId];
          if (!x) return;
          if (this.dataset.mode === "bid") {
            if (this._bidUpsells) delete this._bidUpsells[variantId];
          } else if (x.key) {
            var root =
              (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ||
              "/";
            try {
              await fetch(root + "cart/change.js", {
                method: "POST",
                headers: { "Content-Type": "application/json", Accept: "application/json" },
                body: JSON.stringify({ id: x.key, quantity: 0 }),
              });
            } catch (err) {
              return;
            }
          }
          delete this._sessionExtras[variantId];
          this.renderExtras();
        }

        // force: rimozione decisa dal configuratore (pedale sostituito,
        // "Nessun pedale", X nel riepilogo), non un click sulla card. Salta il
        // cooldown anti doppio-tap, che altrimenti la scarterebbe se arriva
        // entro 600ms dall'aggiunta.
        _toggleBidUpsell(card, force) {
          var id = card.dataset.upsellVariantId;
          if (!id) return;
          // Stesso anti doppio-tap del percorso carrello (_toggleCartUpsell):
          // senza cooldown il secondo click di un doppio tap faceva da toggle
          // inverso (add+remove in <1s, card che "non si seleziona mai") e ogni
          // ciclo on/off emetteva un "bid upsell added" in più — pattern
          // visibile in PostHog come capture ripetute sulla stessa variante a
          // 1-2s di distanza. Qui il toggle è sincrono, quindi il cooldown
          // parte dal toggle stesso (nel percorso cart parte a fine richiesta).
          this._upsellCooldownUntil = this._upsellCooldownUntil || {};
          if (!force && (this._upsellCooldownUntil[id] || 0) > Date.now()) return;
          this._upsellCooldownUntil[id] = Date.now() + 600;
          this._bidUpsells = this._bidUpsells || {};
          if (this._bidUpsells[id]) {
            delete this._bidUpsells[id];
            delete this._sessionExtras[id];
          } else {
            var cents = parseInt(card.dataset.upsellPrice || "0", 10);
            this._bidUpsells[id] = true;
            track("bid upsell added", {
              variant_id: String(this.dataset.bikeVariantId),
              upsell_variant_id: String(id),
              price_cents: cents,
            });
            this._sessionExtras[id] = {
              title: this._upsellCardTitle(card),
              cents: cents,
            };
          }
          this.renderExtras();
        }

        // Compra-ora ("Complete your bike"): il click sulla card fa add/remove
        // diretto sul carrello reale (niente modale prodotto). Lo stato
        // "selezionata" (pallino) riflette la presenza in carrello, reso da
        // renderExtras() via _sessionExtras. Le voci aggiunte in questa sessione
        // (sessionAdded:true) sono provvisorie: removeSessionExtras() le toglie
        // all'abbandono; quelle già presenti all'apertura (sessionAdded:false)
        // restano, ma un click esplicito le rimuove comunque.
        async _toggleCartUpsell(card, force) {
          var id = card.dataset.upsellVariantId;
          if (!id) return;
          // .loading porta pointer-events:none: fa da guardia anti doppio-click
          // mentre la richiesta è in volo (+ mostra lo spinner della card).
          if (card.classList.contains("loading")) return;
          // Cooldown POST-richiesta (video Alberto, 2026-07-27): un secondo
          // click a pochi ms dal primo (doppio tap trackpad/mouse) arrivava
          // giusto DOPO che .loading era stata tolta e faceva da toggle
          // inverso, annullando l'add appena riuscito - sembrava "non si
          // aggiunge mai" perché il ciclo add+remove è troppo veloce da
          // notare. Ignora un secondo toggle sulla stessa variante entro
          // 600ms dalla fine del precedente.
          this._upsellCooldownUntil = this._upsellCooldownUntil || {};
          if (!force && (this._upsellCooldownUntil[id] || 0) > Date.now()) return;
          var extras = this._sessionExtras || {};
          var root =
            (window.Shopify &&
              window.Shopify.routes &&
              window.Shopify.routes.root) ||
            "/";
          card.classList.add("loading");
          try {
            if (Object.prototype.hasOwnProperty.call(extras, id)) {
              // RIMOZIONE dal carrello (vale sia per gli add di sessione sia
              // per gli item già presenti all'apertura).
              var key = extras[id].key;
              if (key) {
                await fetch(root + "cart/change.js", {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                  },
                  body: JSON.stringify({ id: key, quantity: 0 }),
                });
              }
              delete this._sessionExtras[id];
            } else {
              // AGGIUNTA al carrello (variante di default della card).
              // L'accessorio nasce SEMPRE associato alla bici
              // (__bkrm_parent_variant), anche nel flusso buy da PDP dove la
              // bici entra in carrello solo al commit: la cart page lo
              // raggruppa sotto la bici e la delete cascade lo rimuove con
              // lei. Prima il buy flow contava solo sul retro-tag al commit,
              // che fallisce in silenzio o non parte se si lascia la pagina
              // col drawer aperto (Slack Tommaso 2026-10-16, pedali orfani).
              // Se la bici non arriva mai in carrello, la pulizia orfani di
              // app.js toglie la riga al primo caricamento di pagina; se
              // succede con il drawer ancora aperto in un'altra scheda,
              // _restoreSessionExtras la rimette al commit.
              var addItem = {
                id: id,
                quantity: 1,
                properties: {
                  __bkrm_parent_variant: String(this.dataset.bikeVariantId),
                },
              };
              // Prima della richiesta: una pulizia in un'altra scheda che
              // vede gia' la riga ha sempre un orario successivo.
              var addStartedAt = Date.now();
              var resAdd = await fetch(root + "cart/add.js", {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Accept: "application/json",
                },
                body: JSON.stringify({ items: [addItem] }),
              });
              if (resAdd.ok) {
                var added = await resAdd.json();
                var line = added.items ? added.items[0] : added;
                this._sessionExtras[id] = {
                  title:
                    this._upsellCardTitle(card) ||
                    (line && line.product_title) ||
                    "",
                  cents:
                    line && typeof line.final_line_price === "number"
                      ? line.final_line_price
                      : parseInt(card.dataset.upsellPrice || "0", 10),
                  key: line ? line.key : null,
                  sessionAdded: true,
                  // Per _restoreSessionExtras: conta solo una pulizia orfani
                  // successiva a questo add.
                  addedAt: addStartedAt,
                };
                track("configurator upsell added", {
                  variant_id: String(this.dataset.bikeVariantId),
                  upsell_variant_id: String(id),
                });
                if (this._upsellErrorShown) {
                  this.hideError();
                  this._upsellErrorShown = false;
                }
              } else {
                // Rifiuto di Shopify (tipicamente 422: variante esaurita dopo
                // il render della pagina, che filtra i sold-out solo lato
                // Liquid). Prima il click finiva qui senza alcun feedback
                // (Slack Alberto S., Assioma PRO RS-2, 2026-10-14). La
                // `description` del 422 arriva già nella lingua dello store.
                var failDescription = await cartErrorDescription(resAdd);
                this.showError(failDescription);
                this._upsellErrorShown = true;
                track("configurator upsell add failed", {
                  variant_id: String(this.dataset.bikeVariantId),
                  upsell_variant_id: String(id),
                  status: resAdd.status,
                  description: failDescription,
                });
              }
            }
          } catch (e) {
            /* best-effort: su errore rete lo stato resta invariato */
          } finally {
            card.classList.remove("loading");
            this._upsellCooldownUntil[id] = Date.now() + 600;
          }
          this.renderExtras();
          // Badge congelato al conteggio d'apertura: gli upsell sono provvisori
          // fino al "Procedi" (come per gli add via quick-view).
          if (this._baseItemCount != null) {
            this.updateCartCount(this._baseItemCount);
          }
          document.dispatchEvent(
            new CustomEvent("cart:refresh", { bubbles: true }),
          );
        }

        // All'apertura: se un prodotto upsell è GIÀ nel carrello la sua card
        // deve risultare selezionata (feedback Alberto). Segna quelle voci come
        // sessionAdded:false così l'abbandono non le rimuove; solo un click
        // esplicito le toglie. Fire-and-forget da open().
        //
        // Vale ANCHE in edit-mode (tasto "Edit" dal carrello): senza questo, un
        // accessorio già a carrello appariva NON selezionato → ri-cliccarlo ne
        // aggiungeva un duplicato, e lo stato del pallino era slegato dal
        // carrello reale (comportamento confuso segnalato da Alberto). In edit
        // mode il carrello è quello reale, quindi anche gli add restano (vedi
        // removeSessionExtras, no-op in edit).
        async _seedCartUpsells() {
          var root =
            (window.Shopify &&
              window.Shopify.routes &&
              window.Shopify.routes.root) ||
            "/";
          var cart;
          try {
            var res = await fetch(root + "cart.js", {
              headers: { Accept: "application/json" },
            });
            if (!res.ok) return;
            cart = await res.json();
          } catch (e) {
            return;
          }
          if (!cart || !cart.items) return;
          var cards = this.querySelectorAll(
            "quick-view[data-upsell-variant-id]",
          );
          var cardById = {};
          for (var c = 0; c < cards.length; c++) {
            // Una card con selettore e nessuna taglia scelta ha id VUOTO: non
            // va indicizzata, o una riga carrello senza id la "troverebbe".
            if (cards[c].dataset.upsellVariantId)
              cardById[cards[c].dataset.upsellVariantId] = cards[c];
            // Card con selettore: in carrello puo' esserci una qualunque
            // delle sue varianti (il casco M, non la S di partenza).
            String(cards[c].dataset.upsellVariantIds || "")
              .split(",")
              .filter(Boolean)
              .forEach(function (vid) {
                if (!cardById[vid]) cardById[vid] = cards[c];
              });
          }
          var changed = false;
          for (var i = 0; i < cart.items.length; i++) {
            var it = cart.items[i];
            // Salta la config gestita dal configuratore (bici/kit/box).
            if (it.properties && it.properties.__bkrm_drawer_select) continue;
            // Salta gli accessori già associati a un'ALTRA bici: non devono
            // apparire selezionati qui (né essere rimovibili/ri-parentati da
            // questa sessione).
            if (
              it.properties &&
              it.properties.__bkrm_parent_variant &&
              String(it.properties.__bkrm_parent_variant) !==
                String(this.dataset.bikeVariantId)
            )
              continue;
            var vid = String(it.variant_id || it.id || "");
            if (!vid) continue;
            var card = cardById[vid];
            if (!card) continue;
            if (Object.prototype.hasOwnProperty.call(this._sessionExtras, vid))
              continue;
            // Riallinea la card alla variante che e' davvero in carrello.
            var picker = card.querySelector("[data-cfg-upsell-variant]");
            if (picker && card.dataset.upsellVariantId !== vid) {
              picker.value = vid;
              this._applyCardVariant(card, picker);
            }
            this._sessionExtras[vid] = {
              title: this._upsellCardTitle(card) || it.product_title || it.title || "",
              cents:
                typeof it.final_line_price === "number"
                  ? it.final_line_price
                  : (it.price || 0) * (it.quantity || 1),
              key: it.key || null,
              sessionAdded: false,
            };
            changed = true;
          }
          if (changed && this.classList.contains("is-open")) this.renderExtras();
        }


        // Login richiesto per collegare il bid al customer bike-seller.
        // Se il tema ha ancora la modale login classica → login INLINE (storico,
        // senza lasciare il drawer): la modal si apre SOPRA il configuratore e il
        // submit del form viene intercettato via fetch; a login riuscito si
        // aggiorna il customer id e si prosegue allo step kit/box senza navigare.
        // Con i NEW CUSTOMER ACCOUNTS quella modale NON esiste più (#PopupModal-
        // login-header assente): invece di chiudere il drawer, si naviga al login
        // degli account con return alla PDP + ?value=<offerta>. Al ritorno, la
        // logica di resume in dialog-bid.liquid (?value= / bkrmBidResume) riapre
        // il configuratore direttamente allo step kit/box.
        _openBidLogin() {
          // Salvato PRIMA di qualsiasi navigazione: vale sia per il login inline
          // sia per il redirect esterno. sessionStorage è per-origine e sopravvive
          // all'andata/ritorno sullo stesso dominio, anche se il return_url venisse
          // ignorato dalla config account.
          try {
            sessionStorage.setItem(
              "bkrmBidResume",
              JSON.stringify({
                path: window.location.pathname,
                value: this.bidState.value || 0,
                ts: Date.now(),
              })
            );
          } catch (e) {
            /* storage pieno/negato: resta il fallback via return_url */
          }

          var accountLink = document.querySelector(".thb-mobile-account-link");
          var loginHref = (accountLink && accountLink.getAttribute("href")) || "/account/login";

          // Un link account ASSOLUTO (https://account.bike-room.com) significa
          // New Customer Accounts, e lì la modale classica qui sotto è un vicolo
          // cieco: theme.liquid registra un listener in CAPTURE su document che
          // trasforma ogni click su .thb-secondary-login — cioè esattamente
          // opener.children[0] — in `window.location.href = <link account>`.
          // Quindi la modale non si apre mai: la pagina naviga sull'app account
          // e l'utente finisce su Orders/Profile ("Ready to shop?"), che è il
          // sintomo che questa PR doveva togliere. Anche riuscendo ad aprirla non
          // servirebbe: il POST /account/login che incapsula non autentica sui
          // New Customer Accounts (è il motivo per cui theme.liquid lo dirotta).
          // Il login che funziona è l'overlay inline di Oxi, qui sotto: resta
          // sulla PDP con l'offerta viva dietro. Stesso segnale usato da
          // storefrontLoginUrl, così Customer Accounts CLASSIC — dove la modale
          // funziona davvero — non regredisce.
          var hostedAccounts = /^https?:/i.test(loginHref);

          var opener = document.querySelector("#PopupModal-login-header-button");
          var header = document.querySelector("#PopupModal-login-header");

          if (!hostedAccounts && opener && header) {
            // --- Percorso storico: modale login classica presente (inline) ---
            var i = header.querySelector("input[name='return_to']");
            if (i && i.value.indexOf("value=") === -1)
              i.value = i.value + "?value=" + (this.bidState.value || "");
            header.classList.add("product-popup-modal--above-configurator");
            this._wireBidLoginForm(header);
            if (opener.children[0]) opener.children[0].click();
            return;
          }

          // --- New Customer Accounts + Oxi Social Login: apri il POPUP di Oxi
          // INLINE, senza lasciare la PDP (era il bug: this.close() silenzioso).
          // Oxi registra un listener in fase di CAPTURE su document che
          // intercetta i click su link con href "account" — SOLO se l'utente è
          // sloggato e NON è già su una pagina account — e apre #oxi-gp-overlay
          // (z-index 999998, quindi sopra il drawer), registrando l'URL corrente
          // come initiated_from_url → a login fatto Oxi torna QUI (sulla PDP).
          // Al reload, bkrmBidResume + lo shim di dialog-bid riaprono lo step 2
          // con l'offerta; theme.liquid {% if customer %} copre il caso in cui
          // si atterri su un'altra pagina bike-room.com. Sintetizziamo il click.
          // accountLink / loginHref sono già stati letti sopra, per decidere se
          // la modale classica fosse praticabile.
          var probe = document.createElement("a");
          probe.href = loginHref;
          probe.setAttribute("aria-hidden", "true");
          probe.style.cssText = "position:fixed;left:-9999px;top:0;width:0;height:0;";
          // Il probe non deve MAI navigare da solo: se Oxi intercetta fa lui
          // preventDefault + stopImmediatePropagation (questo listener non parte);
          // se Oxi è assente lo blocchiamo qui e navighiamo esplicitamente sotto.
          probe.addEventListener("click", function (ev) {
            ev.preventDefault();
          });
          document.body.appendChild(probe);
          probe.dispatchEvent(
            new MouseEvent("click", {
              bubbles: true,
              cancelable: true,
              composed: true,
              view: window,
            })
          );
          var opened = !!document.getElementById("oxi-gp-overlay");
          probe.remove();
          if (opened) return;

          // Fallback (popup Oxi non disponibile): NON navigare sull'app account,
          // che scarica l'utente su Orders/Profile fuori dal funnel. Passa dal
          // login storefront con return_to sulla PDP + ?value=, così a login
          // fatto si torna direttamente qui e lo shim di dialog-bid riapre lo
          // step 2 con l'offerta. Il resume in sessionStorage resta come rete.
          var back =
            window.location.pathname + "?value=" + (this.bidState.value || "");
          window.location.assign(storefrontLoginUrl(back, loginHref) || loginHref);
        }

        _wireBidLoginForm(header) {
          var self = this;
          var form = header.querySelector('form[action*="/account/login"]');
          if (!form || form._bkrmBidWired) return;
          form._bkrmBidWired = true;
          form.addEventListener("submit", function (e) {
            // Intercetta solo quando il login parte dal flusso bid (drawer
            // aperto in bid mode); dal login dell'header resta il POST classico.
            if (!(self.dataset.mode === "bid" && self.classList.contains("is-open"))) return;
            e.preventDefault();
            self._submitBidLogin(form, header);
          });
        }

        async _submitBidLogin(form, header) {
          var submitBtn = form.querySelector('button[type="submit"]');
          if (submitBtn) submitBtn.disabled = true;
          var errEl = form.querySelector("[data-bid-login-error]");
          if (!errEl) {
            errEl = document.createElement("div");
            errEl.setAttribute("data-bid-login-error", "");
            errEl.className = "form-notification error";
            errEl.hidden = true;
            form.insertBefore(errEl, form.firstChild);
          }
          errEl.hidden = true;
          try {
            var res = await fetch(form.action, {
              method: "POST",
              body: new URLSearchParams(new FormData(form)),
              headers: { "Content-Type": "application/x-www-form-urlencoded" },
              credentials: "same-origin",
              redirect: "follow",
            });
            var landed = res.url || "";
            if (landed.indexOf("/challenge") !== -1) {
              // Challenge anti-bot: non superabile via fetch — submit nativo
              // (naviga); al ritorno ci pensano return_to/resume.
              form._bkrmBidWired = "bypass";
              form.submit();
              return;
            }
            if (res.ok && landed.indexOf("/account/login") === -1) {
              // Sessione creata senza navigare: recupera il customer id dal
              // markup fresco della PDP (data-customer-id del configuratore).
              var cid = "";
              try {
                var html = await (
                  await fetch(window.location.pathname, { credentials: "same-origin" })
                ).text();
                var m = html.match(/data-customer-id="(\d+)"/);
                if (m) cid = m[1];
              } catch (e) {
                /* fallback sotto */
              }
              if (!cid) {
                // Senza id il payload bid non è costruibile: un solo reload
                // sulla PDP stessa, il resume riapre allo step 2.
                window.location.reload();
                return;
              }
              this.dataset.customerId = cid;
              try {
                sessionStorage.removeItem("bkrmBidResume");
              } catch (e) {}
              var closeBtn = header.querySelector("#ModalClose-login-header");
              if (closeBtn) closeBtn.click();
              else if (typeof header.hide === "function") header.hide();
              this._setStep("kit");
              return;
            }
            // Credenziali errate (Shopify ripresenta la login page).
            errEl.textContent =
              this.dataset.tLoginError || "Login failed — check your email and password.";
            errEl.hidden = false;
          } catch (e) {
            errEl.textContent =
              this.dataset.tLoginError || "Login failed — please try again.";
            errEl.hidden = false;
          } finally {
            if (submitBtn) submitBtn.disabled = false;
          }
        }

        // Diagnostica del fallimento offerta (Asana 1216905741858862). Il
        // POST /api/v1/bid/request vive su bike-seller, fuori dal tema: senza
        // un evento qui l'unica traccia di un'offerta rifiutata era un
        // console.error nella console del cliente, e su PostHog il fallimento
        // era indistinguibile da un abbandono allo step review (il successo
        // emette `bid placed`, l'errore niente). Registra SEMPRE, anche
        // quando il backend non risponde in JSON.
        _reportBidFailure(detail) {
          var kitBtn = this.querySelector(
            '.cd-config-row--selected .cd-config-row-main[data-cd-config-group="service-pack"]'
          );
          var boxBtn = this.querySelector(
            '.cd-config-row--selected .cd-config-row-main[data-cd-config-group="box"]'
          );
          var err = extractBidError(detail.body);
          var props = {
            reason: detail.reason,
            status: detail.status,
            endpoint: detail.endpoint || null,
            error_code: err.code || null,
            error_message: detail.message || err.message || null,
            // Corpo troncato: i backend rispondono anche con HTML (502/504 di
            // un proxy) — serve a capire QUALE componente ha risposto.
            error_body: truncateForAnalytics(detail.body),
            variant_id: String(this.dataset.bikeVariantId),
            bid_amount: this.bidState ? this.bidState.value : null,
            list_price: this._bidPriceMajor(),
            currency: this.dataset.currency,
            market: this.dataset.market,
            country_code: this.dataset.country,
            expiration_days: this.bidState ? this.bidState.expiration : null,
            kit_variant_id: kitBtn ? kitBtn.dataset.variantId : null,
            box_key: boxBtn ? boxBtn.dataset.handle : null,
            upsell_count: Object.keys(this._bidUpsells || {}).length,
            // Serve a misurare se il retry salva davvero dei casi, e a
            // riconoscere eventuali offerte duplicate nate da un retry.
            retried: !!detail.retried,
          };
          console.error("Bid request failed", props);
          track("bid request failed", props);
        }

        // Disponibilità LIVE della bici, non quella incorporata nella pagina.
        // Il percorso add-to-cart fa già questo controllo prima di aprire il
        // configuratore (product.js, ramo bkrmConfigurator: refreshAvailability
        // di PR #81); il percorso bid apre lo STESSO configuratore da un'altra
        // porta e se l'era perso. Fra apertura del bid e "Place your bid"
        // passano minuti: la bici può sparire nel frattempo.
        //
        // Ritorna true / false / null. null = non verificabile (rete ko,
        // handle assente): in quel caso NON si blocca l'offerta — un controllo
        // diagnostico non deve mai impedire una vendita legittima.
        async _isBikeStillAvailable() {
          var sel = document.querySelector("variant-selects[data-url]");
          var url = sel && sel.dataset.url;
          if (!url) return null;
          try {
            var res = await fetch(url + ".js", { cache: "no-store" });
            if (!res.ok) return null;
            var product = await res.json();
            if (!product || !Array.isArray(product.variants)) return null;
            var id = String(this.dataset.bikeVariantId);
            var match = product.variants.filter(function (v) {
              return String(v.id) === id;
            })[0];
            // Variante sparita dal catalogo (bici venduta e rimossa): è il
            // caso del Trek Madone della 3ª segnalazione.
            if (!match) return false;
            return match.available !== false;
          } catch (e) {
            return null;
          }
        }

        async _placeBid() {
          if (!this.bidState || !this.bidState.value || !this.bidState.expiration) return;
          if (!this.dataset.customerId) {
            this._openBidLogin();
            return;
          }
          this._submitting = true;
          this.syncProceed();
          this.hideError();
          // Dichiarato fuori dal try: il ramo catch deve poter dire QUALE dei
          // due endpoint stava chiamando quando la rete è caduta.
          var endpoint = "/api/v1/bid/request";
          var retried = false;
          try {
            if ((await this._isBikeStillAvailable()) === false) {
              this._reportBidFailure({
                reason: "variant_unavailable",
                status: 0,
                endpoint: null,
              });
              this.showError(this.dataset.tErrorUnavailable);
              return;
            }
            var kitBtn = this.querySelector(
              '.cd-config-row--selected .cd-config-row-main[data-cd-config-group="service-pack"]'
            );
            var boxBtn = this.querySelector(
              '.cd-config-row--selected .cd-config-row-main[data-cd-config-group="box"]'
            );
            var boxKey = boxBtn ? boxBtn.dataset.handle : null;
            var body = window.BkrmBidOffer.buildBidPayload({
              shopifyUserId: this.dataset.customerId,
              variantId: this.dataset.bikeVariantId,
              value: this.bidState.value,
              currency: this.dataset.currency,
              expiration: this.bidState.expiration,
              service: this.state.kitVariantId,
              boxKey: boxKey,
              boxVariantId: this.state.boxVariantId,
              hasBox: boxKey !== "standard-box",
              hasWarranty: false,
              isInstantBuy: false,
              // Gli addon viaggiano come upsell: upsellId e' una lista di
              // varianti che bike-seller mette nel draft order, ed e' quello
              // che un addon e'. Senza, in bid la scelta si vedeva nel recap
              // e poi spariva dall'ordine.
              upsellId: Object.keys(this._bidUpsells || {}).concat(
                addonVariantIds(this.state).map(String),
              ),
              compareAtPrice: this._bidPriceMajor(),
              marketName: this.dataset.market,
              countryCode: this.dataset.country,
              fxRate: window.Shopify && window.Shopify.currency && window.Shopify.currency.rate,
              now: Date.now(),
            });

            // Il fallback NON è produzione: data-bid-api-base è emesso da
            // snippets/bkrm-api-host.liquid e instradato per ambiente, quindi
            // se manca siamo davanti a un errore di rendering — e "manca" non
            // deve mai voler dire "scrivi in produzione" (era il bug chiuso
            // dal routing: da staging si scriveva sul backend reale).
            // .invalid non risolve mai (RFC 2606): la POST muore subito.
            // Raggiungibile solo in edit-mode, dove il bid non parte.
            var base = this.dataset.bidApiBase || "https://unrouted-service.bkrm.invalid";
            var postJson = async function (url) {
              var res = await fetch(url, {
                method: "POST",
                headers: { "Content-Type": "application/json", Accept: "application/json" },
                body: JSON.stringify(body),
              });
              var json = null;
              try {
                json = await res.json();
              } catch (e) {
                /* ignore */
              }
              return { res: res, json: json };
            };

            // Un SOLO secondo tentativo, e solo dove è improbabile che la
            // richiesta sia arrivata all'applicazione: errore di rete
            // (preflight CORS bloccato, offline, adblock) e 429/502/503, che
            // di norma vengono dal davanti (Cloudflare/proxy) prima che il
            // backend veda qualcosa.
            //
            // 504 e 4xx sono ESCLUSI di proposito: un gateway timeout
            // significa "upstream troppo lento", quindi l'offerta può essere
            // già stata creata e un secondo tentativo la duplicherebbe.
            // Rischio residuo noto: una connessione caduta DOPO che il
            // backend ha processato è indistinguibile da una mai arrivata.
            var self = this;
            var postWithRetry = async function (url) {
              try {
                var first = await postJson(url);
                if (BID_RETRY_STATUSES.indexOf(first.res.status) === -1) return first;
              } catch (e) {
                /* rete: ritenta sotto */
              }
              retried = true;
              await new Promise(function (r) {
                setTimeout(
                  r,
                  typeof self._bidRetryDelayMs === "number"
                    ? self._bidRetryDelayMs
                    : BID_RETRY_DELAY_MS
                );
              });
              return postJson(url);
            };

            var out = await postWithRetry(base + endpoint);
            if (out.res.status === 404) {
              endpoint = "/api/v1/shopify/bid/request";
              out = await postJson(base + endpoint);
              var invoiceUrl =
                out.json &&
                out.json.data &&
                out.json.data.draftOrderCreate &&
                out.json.data.draftOrderCreate.draftOrder &&
                out.json.data.draftOrderCreate.draftOrder.invoiceUrl;
              if (invoiceUrl) {
                window.location.href = invoiceUrl;
                return;
              }
            }
            if (out.json && out.json.checkoutUrl) {
              track("bid placed", {
                variant_id: String(this.dataset.bikeVariantId),
                bid_amount: this.bidState.value,
                currency: this.dataset.currency,
                market: this.dataset.market,
                country_code: this.dataset.country,
                has_vat: false,
              });
              window.location.href = out.json.checkoutUrl;
              return;
            }
            this._reportBidFailure({
              // Una 2xx senza checkoutUrl è un contratto rotto fra tema e
              // bike-seller, non un rifiuto dell'offerta: va distinta.
              reason: out.res.status >= 400 ? "http_error" : "no_checkout_url",
              status: out.res.status,
              endpoint: endpoint,
              body: out.json,
              retried: retried,
            });
            this.showError();
          } catch (err) {
            this._reportBidFailure({
              reason: "network_error",
              status: 0,
              endpoint: endpoint,
              message: err && err.message,
              retried: retried,
            });
            this.showError();
          } finally {
            this._submitting = false;
            this.syncProceed();
          }
        }

        // Instant buy: entra nel percorso "compra subito" — va allo step
        // kit/box con la CTA che diventa "Go to checkout" (come la vecchia
        // modal: goToInstantBuy passava dallo step servizi, feedback Alberto).
        instantBuy() {
          this._bidInstant = true;
          this.syncBidOffer(this._bidPriceMajor());
          this._setStep("kit");
        }

        // Instant buy: checkout Shopify nativo con bici + kit + box + addon +
        // upsell. Prima era un permalink (cart/clear + /cart/ID:1,...), che
        // non porta le line properties: le righe entravano senza
        // __bkrm_drawer_select / __bkrm_parent_variant e bkrmPruneCartOrphans
        // (app.js) toglieva kit, box Standard/Safe e upgrade d'officina alla
        // prima pagina del tema, compresa la pagina di login se il checkout
        // chiede l'accesso (segnalazione Luca, verificata il 27/10/2026).
        // Ora le righe sono le stesse dell'acquisto normale (buildAddItems).
        // Gli upsell dell'offerta (in bid stanno anche in _sessionExtras)
        // entrano dopo, uno per uno e agganciati alla bici come al commit
        // normale: nello stesso /cart/add.js un accessorio esaurito faceva
        // fallire tutto senza dire quale. Se uno non entra ci si ferma prima
        // del checkout, col carrello di nuovo vuoto (review della #251).
        async _instantCheckout() {
          if (this._submitting) return;
          this._submitting = true;
          if (this.proceedBtn) this.proceedBtn.disabled = true;
          this.hideError();
          var root =
            (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
          var items = window.BkrmConfigurator.buildAddItems(this.cartState());
          try {
            var resClear = await fetch(root + "cart/clear.js", {
              method: "POST",
              headers: { Accept: "application/json" },
            });
            if (!resClear.ok) throw new Error("cart/clear failed: " + resClear.status);
            var res = await fetch(root + "cart/add.js", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
              },
              body: JSON.stringify({ items: items }),
            });
            if (!res.ok) {
              // Il carrello e' gia' svuotato: senza il motivo di Shopify
              // (es. kit o box non piu' disponibili) ogni nuovo tentativo
              // fallirebbe uguale senza dire quale riga lo blocca.
              var refusal = new Error("cart/add failed: " + res.status);
              refusal.description = await cartErrorDescription(res);
              throw refusal;
            }
            // Upsell dell'offerta: uno per uno e agganciati alla bici, come
            // il ripristino al commit normale. Non passano da
            // _restoreSessionExtras: non sono mai stati in carrello, quindi
            // la pulizia orfani non li ha annotati e li salterebbe. Se uno
            // non entra ci si ferma come al commit normale: il drawer lo
            // contava nel totale e il cliente deve saperlo prima del
            // checkout. Il prossimo tap rifa' clear + add senza i rifiutati.
            var upsells = await this._addExtrasToBike(
              root,
              Object.keys(this._bidUpsells || {}),
            );
            if (upsells.failed.length) {
              // Il carrello e' stato svuotato all'inizio: si svuota di nuovo
              // invece di lasciarci bici, kit e box a prezzo pieno senza
              // checkout. Le line key degli upsell entrati non esistono piu'.
              try {
                await fetch(root + "cart/clear.js", {
                  method: "POST",
                  headers: { Accept: "application/json" },
                });
              } catch (_) {
                /* best-effort: il prossimo tap svuota comunque */
              }
              Object.keys(this._sessionExtras || {}).forEach(function (id) {
                delete this._sessionExtras[id].key;
              }, this);
              this._stopOnFailedExtras(upsells.failed, "bid instant upsell failed");
              return;
            }
            this._holdSubmitWhileLeaving();
            window.location.assign(root + "checkout");
          } catch (err) {
            this.showError(err && err.description);
          } finally {
            if (!this._leaving) this._releaseSubmit();
          }
        }

        open() {
          // Choke point di ogni apertura: standard da product.js (submit CTA),
          // bid da openBid(). Il mode viene dedotto da _openingBid e non da
          // dataset.mode, che a questo punto può ancora contenere il "bid"
          // residuo di un'apertura precedente (il reset avviene sotto).
          track("configurator opened", {
            mode: this._openingBid ? "bid" : "standard",
            edit_mode: this.dataset.editMode === "true",
            // Resume post-login (?value=/sessionStorage): riapertura
            // automatica senza gesto utente — senza questo flag ogni bid
            // di un utente sloggato conterebbe DUE open nel funnel.
            resumed: !!this._openingBidResumed,
            bike_variant_id: this.dataset.bikeVariantId || undefined,
          });
          // Ogni apertura riparte dal primo step: la barra non deve offrire
          // come "gia' visitati" gli step di una sessione precedente, e
          // "Nessun pedale" non sopravvive alla chiusura.
          this._maxStepIdx = 0;
          this._noPedals = false;
          // Apertura standard (add-to-cart): azzera la modalità bid di
          // un'apertura precedente e ripristina la label della CTA.
          // openBid() setta _openingBid prima di delegare qui.
          if (!this._openingBid) {
            delete this.dataset.mode;
            delete this.dataset.step;
            this._bidUpsells = {};
            if (this.proceedBtn && this._proceedLabel)
              this.proceedBtn.textContent = this._proceedLabel;
            var cfgTitle = this.querySelector(".bike-configurator__title");
            if (cfgTitle && this._cfgTitleLabel)
              cfgTitle.textContent = this._cfgTitleLabel;
            this._restoreDefaultServiceVariants();
            // Step wizard per tutti i flussi, edit-mode incluso (richiesta
            // Alberto, review round 2 — prima l'edit restava pannello piatto).
            this._setStep("kit");
          }
          // Suppress border-color transitions for the first couple of frames so
          // that resetting/seeding the kit selection on open never produces a red
          // border flash (e.g. a card server-marked --selected from leftover cart
          // state fading red→grey). Interactive selection transitions are
          // unaffected — the class is removed right after the first paints.
          this.classList.add("is-suppress-anim");
          var self = this;
          requestAnimationFrame(function () {
            requestAnimationFrame(function () {
              self.classList.remove("is-suppress-anim");
            });
          });
          if (this.dataset.editMode === "true") {
            // Edit mode (cart page): the server pre-marks the bike's current
            // kit/box as --selected (cart-drawer-configurator computes
            // is_selected from the cart line items). Seed state from those so
            // "Proceed" is enabled and reflects the current configuration.
            this.seedStateFromSelected();
          } else {
            // PDP: every open starts fresh. Without this, the service/box
            // selection (and aria-pressed/--selected classes) from a previous
            // opening — e.g. before switching size/color — would carry over.
            this.resetSelection();
          }
          // Il blocco Personalizzazione dipende dal kit scelto, e in edit il
          // kit arriva gia' selezionato dal server: senza un click nessuno
          // chiamerebbe _syncCustomBlock e il blocco resterebbe nascosto per
          // sempre. Va quindi sincronizzato a ogni apertura, dopo la semina.
          this._syncCustomBlock();
          // Fresh session: drop any upsells recorded from a previous opening so
          // the recap reflects only what's chosen for this bike right now.
          this._sessionExtras = {};
          this._extrasCents = 0;
          this._resetVariantPickers();
          this._applyPedalFilter("all");
          this._applyCustomFilter("all");
          this._applyAccFilter("all");
          var pedalsDlg = this._pedalsConfirmEl();
          if (pedalsDlg) pedalsDlg.setAttribute("hidden", "");
          // Snapshot the cart-icon count at open. While the drawer is open,
          // accessories added via quick-view are PROVISIONAL (committed only on
          // Proceed), so they must not bump the cart icon — onCartAdded re-freezes
          // the badge to this value, and removeSessionExtras() restores it on exit.
          if (this.dataset.editMode !== "true") {
            this._baseItemCount = this._readCartCount();
          } else {
            this._baseItemCount = null;
          }
          // Always start with the subtotal breakdown collapsed on open.
          var summary = this.querySelector(".bike-configurator__summary");
          var toggleBtn = this.querySelector(".bike-configurator__summary-total-toggle");
          if (summary) summary.classList.remove("bike-configurator__summary--expanded");
          if (toggleBtn) toggleBtn.setAttribute("aria-expanded", "false");
          // Mai aprire con l'alert di uscita ancora visibile.
          this.hideExitAlert();
          this.removeAttribute("hidden");
          this.classList.add("is-open");
          // Scroll-lock: lock BOTH <html> and <body> — on this theme the
          // scroll container is <html>, so locking only <body> left the page
          // behind scrollable (two scrollbars on mobile). overscroll-behavior
          // on the panel body stops scroll-chaining at the edges.
          document.documentElement.classList.add("bike-configurator-open");
          document.body.classList.add("bike-configurator-open");
          // Reset scroll so a previously-scrolled panel doesn't open mid-page
          // (the scroll container is .bike-configurator__body on desktop and
          // .bike-configurator__main on mobile).
          this.resetScroll();
          // Equalize heights on open so the panel is stable on first render.
          if (this._equalizeServiceDescHeights)
            setTimeout(this._equalizeServiceDescHeights, 0);
          // renderExtras() clears any stale upsell rows and calls updateSummary().
          this.renderExtras();
          // Compra-ora: riflette gli upsell già nel carrello come selezionati
          // (async, ridisegna al termine). In bid non serve (nessun carrello).
          if (this.dataset.mode !== "bid") this._seedCartUpsells();
        }

        // Clear any service/box selection so the panel reopens unconfigured.
        resetSelection() {
          this.state = {
            kitVariantId: undefined,
            boxVariantId: undefined,
            addons: {},
          };
          this._addonChoices = {};
          this._customUpgraded = false;
          this._customUpgradeFrom = "base";
          this._noPedals = false;
          // Le card tornano ad "Add" con la select vuota: senza, su PDP
          // riaprendo il drawer si vedrebbe "Remove" su una scelta che lo
          // stato ha gia' dimenticato.
          this.querySelectorAll("[data-cfg-addon]").forEach(function (card) {
            var select = card.querySelector("[data-cfg-addon-select]");
            if (select) select.value = "";
            var add = card.querySelector("[data-cfg-addon-add]");
            if (add) {
              add.setAttribute("aria-pressed", "false");
              var span = add.querySelector("span");
              if (span && add.dataset.tAdd) span.textContent = add.dataset.tAdd;
            }
          });
          this.querySelectorAll(".cd-config-row--selected").forEach(
            function (r) {
              r.classList.remove("cd-config-row--selected");
            },
          );
          this.querySelectorAll(
            '.cd-config-row-main[aria-pressed="true"]',
          ).forEach(function (b) {
            b.setAttribute("aria-pressed", "false");
          });
          this.syncProceed();
        }

        resetScroll() {
          var body = this.querySelector(".bike-configurator__body");
          if (body) body.scrollTop = 0;
          var main = this.querySelector(".bike-configurator__main");
          if (main) main.scrollTop = 0;
        }

        seedStateFromSelected() {
          var self = this;
          ["service-pack", "box"].forEach(function (group) {
            var sel = self.querySelector(
              '.cd-config-row--selected .cd-config-row-main[data-cd-config-group="' +
                group +
                '"]',
            );
            if (!sel) return;
            if (group === "service-pack")
              self.state.kitVariantId = sel.dataset.variantId;
            else self.state.boxVariantId = sel.dataset.variantId;
          });
          // Addon gia' in carrello per questa bici: il Liquid marca la card
          // con data-cfg-addon-selected. Senza questa semina _commitEdit,
          // che rimuove tutti i figli e riaggiunge solo cio' che conosce,
          // li cancellerebbe a ogni Edit senza dirlo a nessuno.
          this.querySelectorAll("[data-cfg-addon]").forEach(function (card) {
            var giaScelta = card.dataset.cfgAddonSelected;
            if (!giaScelta) return;
            var select = card.querySelector("[data-cfg-addon-select]");
            var opt = select
              ? select.querySelector('option[value="' + giaScelta + '"]')
              : null;
            if (!opt) return;
            // La select va riportata sulla variante scelta: altrimenti in
            // edit si legge "Select size..." pur avendo l'addon in carrello,
            // e sembra che non sia stato scelto.
            select.value = giaScelta;
            self._chooseAddon(card.dataset.cfgAddon, opt);
          });
          this.syncProceed();
          this.updateSummary();
        }

        updateSummary() {
          var el = this;
          var bikePrice = parseInt(el.dataset.bikePrice || "0", 10);
          var protectionPrice = parseInt(el.dataset.protectionPrice || "0", 10);

          // Modalità bid: il prezzo bici nel recap è l'OFFERTA corrente e la
          // Rider Protection si ricalcola su di essa (stessa formula
          // floor(price*3/100)+fissa del flusso add-to-cart e della modal).
          var isBid = el.dataset.mode === "bid" && el.bidState;
          if (isBid) {
            bikePrice = Math.round((el.bidState.value || 0) * 100);
            protectionPrice =
              el.dataset.protectionEnabled === "true"
                ? Math.floor((bikePrice * 3) / 100) +
                  parseInt(el.dataset.protectionFixed || "0", 10)
                : 0;
          }

          // Service / Box rows have STATIC left labels ("Service" / "Box",
          // rendered server-side) — they are required, so they read as labels
          // even before a tier is picked. We only fill the price on the right.
          var kitRow = el.querySelector(
            '.cd-config-row--selected .cd-config-row-main[data-cd-config-group="service-pack"]',
          );
          var kitPriceEl = kitRow
            ? kitRow.querySelector(".cd-config-row-price")
            : null;
          var kitPrice = kitPriceEl
            ? parsePriceCents(
                kitPriceEl.dataset.price ?? kitPriceEl.textContent,
              )
            : 0;

          var boxRow = el.querySelector(
            '.cd-config-row--selected .cd-config-row-main[data-cd-config-group="box"]',
          );
          var boxPriceEl = boxRow
            ? boxRow.querySelector(".cd-config-row-price")
            : null;
          var boxPrice = boxPriceEl
            ? parsePriceCents(
                boxPriceEl.dataset.price ?? boxPriceEl.textContent,
              )
            : 0;

          // Pedivella: opzionale, quindi la riga del recap esiste solo quando
          // c'e' una scelta (a differenza di kit/box, che hanno label fisse).
          // Addon (pedivella, pacco pignoni, guarnitura): opzionali, quindi
          // la riga del breakdown compare solo con una scelta fatta. La
          // scelta arriva da _addonChoices e non dal DOM: la card ha una
          // select, non una riga "selezionata".
          var addonsPrice = 0;
          var scelte = el._addonChoices || {};
          ADDON_KEYS.forEach(function (key) {
            var scelta = scelte[key] || null;
            if (scelta) addonsPrice += scelta.cents;
            var riga = el.querySelector('[data-cfg-addon-row="' + key + '"]');
            if (riga) riga.toggleAttribute("hidden", !scelta);
            el.querySelectorAll(
              ".bike-configurator__summary-" + key + "-price",
            ).forEach(function (n) {
              // Senza scelta la cella prezzo si NASCONDE, non si svuota: la
              // riga e' flex con space-between e tre figli, quindi una cella
              // vuota ma disposta spingerebbe la CTA "Add" in mezzo alla riga
              // invece che a filo destro. E' la stessa cosa che fanno le
              // righe Pedals/Upgrade/Accessories, che usano l'attributo
              // hidden sulla cella.
              n.textContent = scelta ? formatCents(scelta.cents) : "";
              n.toggleAttribute("hidden", !scelta);
            });
            el.querySelectorAll(
              ".bike-configurator__summary-" + key + "-value",
            ).forEach(function (n) {
              n.textContent = scelta ? (scelta.plain ? "" : scelta.title) : "-";
            });
          });

          var extrasCents = this._extrasCents || 0;

          // "Sconto bici in omaggio" (solo bid, riga presente solo con il
          // theme setting attivo): gift = compare_at − prezzo kit, cap a
          // offerta − floor. Display-only finché bike-seller non lo
          // materializza nel draft order.
          var giftCents = 0;
          var giftRow = el.querySelector("[data-bid-gift-row]");
          if (isBid && giftRow && kitRow) {
            var compareCents = parseInt(kitRow.dataset.comparePrice || "0", 10);
            var g = window.BkrmBidOffer.computeGift({
              offer: bikePrice / 100,
              floor: el._bidAnchors().floor,
              serviceFull: compareCents / 100,
              add: kitPrice / 100,
            });
            giftCents = Math.round(g.gift * 100);
          }
          if (giftRow) {
            giftRow.toggleAttribute("hidden", !(isBid && giftCents > 0));
            var giftEl = el.querySelector("[data-bid-gift-price]");
            if (giftEl && giftCents > 0)
              giftEl.textContent = "\u2212" + formatCents(giftCents);
          }

          var total =
            bikePrice +
            protectionPrice +
            kitPrice +
            boxPrice +
            addonsPrice +
            extrasCents -
            giftCents;

          // Per l'evento "configurator completed" (extrasProps): lo stesso
          // breakdown del footer, cosi' il valore degli extra tracciato e'
          // quello che il cliente ha visto.
          el._lastBreakdown = {
            bike: bikePrice,
            protection: protectionPrice,
            kit: kitPrice,
            box: boxPrice,
            addons: addonsPrice,
            upsells: extrasCents,
          };

          // "Solo €X in più rispetto alla tua offerta" (UX spec, youPayMore):
          // mostrata insieme alla riga gift — stessa condizione di senso.
          var payMoreNote = el.querySelector("[data-bid-paymore-note]");
          if (payMoreNote) {
            var extraOverOffer = total - bikePrice;
            var showNote = isBid && giftCents > 0 && extraOverOffer > 0;
            payMoreNote.toggleAttribute("hidden", !showNote);
            if (showNote) {
              payMoreNote.textContent = (el.dataset.tPayMore || "").replace(
                "[amount]",
                formatCents(extraOverOffer)
              );
            }
          }

          // Bike + protection rows: shown explicitly so the visible rows sum to
          // the subtotal (otherwise only Service/Box/extras showed and the total
          // didn't add up). Kept in sync with the data attrs on variant change.
          // querySelectorAll (not querySelector): the same price now shows in
          // TWO places - the footer's collapsible breakdown AND the review
          // step's "Your configuration" list (product-configurator.liquid) -
          // both share these class names so a single computation updates both.
          var summaryBike = el.querySelectorAll(
            ".bike-configurator__summary-bike-price",
          );
          var protectionRow = el.querySelector(
            ".bike-configurator__summary-protection-row",
          );
          var summaryProtection = el.querySelectorAll(
            ".bike-configurator__summary-protection-price",
          );
          var summaryKit = el.querySelectorAll(
            ".bike-configurator__summary-kit-price",
          );
          var summaryBox = el.querySelectorAll(
            ".bike-configurator__summary-box-price",
          );
          var summaryKitValue = el.querySelectorAll(
            ".bike-configurator__summary-kit-value",
          );
          var summaryBoxValue = el.querySelectorAll(
            ".bike-configurator__summary-box-value",
          );
          var summaryTotal = el.querySelector(
            ".bike-configurator__summary-total-value",
          );

          summaryBike.forEach(function (n) { n.textContent = formatCents(bikePrice); });
          summaryProtection.forEach(function (n) { n.textContent = formatCents(protectionPrice); });
          if (protectionRow)
            protectionRow.toggleAttribute("hidden", protectionPrice <= 0);
          summaryKit.forEach(function (n) {
            n.textContent = kitRow ? formatCents(kitPrice) : "—";
          });
          summaryBox.forEach(function (n) {
            n.textContent = boxRow ? formatCents(boxPrice) : "—";
          });
          // Review-step "Your configuration" list: shows the selected tier's
          // NAME (e.g. "Ready-to-ride"), not just its price - read from the
          // row's own data-title (set server-side in cart-drawer-configurator.liquid).
          summaryKitValue.forEach(function (n) {
            n.textContent = kitRow ? kitRow.dataset.title || "—" : "—";
          });
          summaryBoxValue.forEach(function (n) {
            n.textContent = boxRow ? boxRow.dataset.title || "—" : "—";
          });
          // Footer breakdown (Figma 6684-5073): la riga mostra il NOME
          // dell'opzione selezionata ("Base", "BKRM Eco Box"), con fallback
          // all'etichetta generica finché non c'è selezione.
          el.querySelectorAll(".bike-configurator__summary-kit-label").forEach(
            function (n) {
              // "Kit Plus", non solo "Plus" (Davide, 23/10/2026): stesso nome
              // nello scontrino del riepilogo.
              n.textContent = kitRow && kitRow.dataset.title
                ? kitName(n.dataset.defaultLabel, kitRow.dataset.title)
                : n.dataset.defaultLabel || n.textContent;
            },
          );
          el.querySelectorAll(".bike-configurator__summary-box-label").forEach(
            function (n) {
              n.textContent =
                (boxRow && boxRow.dataset.title) || n.dataset.defaultLabel || n.textContent;
            },
          );
          if (summaryTotal) summaryTotal.textContent = formatCents(total);

          // "Totale bici · N extra": quante scelte opzionali sono nel totale
          // (prodotti degli step + addon di officina). Kit e box non contano:
          // sono obbligatori, quindi non sono "extra".
          var extraCount =
            Object.keys(this._sessionExtras || {}).length +
            Object.keys(scelte).filter(function (k) {
              return !!scelte[k];
            }).length;
          var countEl = el.querySelector("[data-cfg-extra-count]");
          if (countEl) {
            countEl.hidden = extraCount === 0;
            countEl.textContent = extraCount
              ? (el.dataset.tExtraCount || "[n] extra").replace("[n]", String(extraCount))
              : "";
          }

          this._updateReviewUpsellSlots();
        }

        // Review step "Your configuration" list: PEDALS/UPGRADE YOUR RIDE/
        // ACCESSORIES rows show EVERY selected item's title in each of the 3
        // upsell carousel slots (comma-separated - a slot can have more than
        // one item added, e.g. two accessories), or "-" if none. Each
        // carousel's list container id is "cd-upsell-list-<slot>-<bikeVariantId>"
        // (product-configurator.liquid), so the slot number is read straight
        // off the id rather than tagging every _sessionExtras entry with it.
        _updateReviewUpsellSlots() {
          var self = this;
          [1, 2, 3].forEach(function (slot) {
            var valueEl = self.querySelector(
              '.bike-configurator__review-row-value[data-cfg-review-slot="' + slot + '"]',
            );
            if (!valueEl) return;
            var list = self.querySelector(
              '.cart-drawer-upsell-list[id^="cd-upsell-list-' + slot + '-"]',
            );
            var added = list
              ? list.querySelectorAll("quick-view.is-upsell-added[data-upsell-title]")
              : [];
            var titles = Array.prototype.map.call(added, function (card) {
              return self._upsellCardTitle(card);
            });
            // "Nessun pedale" e' una scelta a tutti gli effetti: la riga
            // Pedali la mostra e non propone "Aggiungi" (handoff, sezione 9).
            var pedalsSlot = list && list.closest('[data-cfg-step-panel="pedals"]');
            if (pedalsSlot && !titles.length && self._noPedals) {
              titles = [self.dataset.tNoPedals || "No pedals"];
            }
            valueEl.textContent = titles.length ? titles.join(", ") : "-";
            // Prezzo complessivo dello slot (feedback Mattia): somma i prezzi
            // delle card aggiunte, letti dalla card stessa (data-upsell-price,
            // già in centesimi) - stesso dato che alimenta il totale del footer.
            var priceEl = self.querySelector(
              '[data-cfg-review-price-slot="' + slot + '"]',
            );
            if (priceEl) {
              var totalCents = Array.prototype.reduce.call(
                added,
                function (sum, card) {
                  return sum + parseInt(card.dataset.upsellPrice || "0", 10);
                },
                0,
              );
              // hidden (non solo testo vuoto): con lo slot vuoto la riga mostra
              // la CTA "Add" al suo posto - un 3° flex item vuoto sposterebbe
              // l'Add al centro invece che a destra (space-between).
              priceEl.hidden = added.length === 0;
              priceEl.textContent = added.length ? formatCents(totalCents) : "";
            }
          });
          this._updateReviewAddButtons();
        }

        // CTA "Add" del recap: visibile SOLO sulle righe ancora vuote, per
        // tutte e cinque (kit, box, pedali, upgrade, accessori) — prima il
        // toggle esisteva solo per i 3 slot upsell, quindi l'Add di kit/box
        // non sarebbe mai comparso. È l'unico comando della riga: il
        // contenitore non è più un bottone (non sembrava cliccabile e
        // confondeva), quindi una riga già compilata non offre nulla da
        // premere e si cambia idea con il Back.
        // "Vuota" = il placeholder che stampano Liquid ("—") e
        // _updateReviewUpsellSlots ("-"), o nessun testo.
        _updateReviewAddButtons() {
          var VUOTI = ["", "-", "\u2013", "\u2014"];
          this.querySelectorAll(".bike-configurator__review-row").forEach(
            function (row) {
              var addBtn = row.querySelector("[data-cfg-review-add]");
              if (!addBtn) return;
              var valueEl = row.querySelector(
                ".bike-configurator__review-row-value",
              );
              var testo = valueEl ? valueEl.textContent.trim() : "";
              var vuota = VUOTI.indexOf(testo) !== -1;
              addBtn.hidden = !vuota;
              // "Modifica" e' il complemento di "Aggiungi": sulle righe
              // compilate porta allo stesso step.
              var editBtn = row.querySelector("[data-cfg-review-edit]");
              if (editBtn) editBtn.hidden = vuota;
            },
          );
          this._renderReceipt();
        }

        // Riepilogo "scontrino" (scelto da Davide il 20/10/2026): le voci
        // scelte in una card, ognuna porta al suo step (data-cfg-summary-jump,
        // stesso handler delle vecchie righe); sotto, "Aggiungi ancora" con una
        // chip per ogni step facoltativo ancora vuoto. Kit e box ci sono
        // sempre: senza, "Continua" non porta fin qui.
        _renderReceipt() {
          var box = this.querySelector("[data-cfg-receipt]");
          if (!box) return;
          var self = this;
          var t = box.dataset;
          var esc = function (s) {
            return String(s == null ? "" : s).replace(/[&<>"]/g, function (c) {
              return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c];
            });
          };
          var prezzo = function (cents) {
            return cents > 0 ? formatCents(cents) : t.tFree || "Free";
          };
          var righe = [];
          // tipo: "upg" (lavori d'officina e pellicola, che applichiamo noi),
          // "acc" (pedali, accessori, migliora), vuoto per kit e box.
          // Ordine del percorso (Davide, 23/10/2026): kit, Personalizza,
          // pedali, accessori, migliora, box, pellicola. Dentro uno step
          // resta l'ordine in cui le voci sono state scelte.
          var RANGO = { kit: 0, custom: 1, pedals: 2, accessories: 3, upgrade: 4, box: 5 };
          var riga = function (testo, cents, step, tipo, rango) {
            righe.push({
              testo: testo,
              cents: cents,
              step: step,
              tipo: tipo || "",
              rango: rango != null ? rango : step in RANGO ? RANGO[step] : 3,
              n: righe.length,
            });
          };
          var scelta = function (group, step) {
            var b = self.querySelector(
              '.cd-config-row--selected .cd-config-row-main[data-cd-config-group="' + group + '"]',
            );
            if (!b) return;
            var p = b.parentElement.querySelector(".cd-config-row-price");
            var cents = p ? parseInt(p.dataset.price || "0", 10) : 0;
            var nome = b.dataset.title || "";
            riga(group === "service-pack" ? kitName(t.lKit, nome) : nome, cents, step);
          };

          // Stesso ordine e stessi nomi del pannello del totale (feedback
          // 23/10/2026: kit, box, upgrade, pellicola, poi i prodotti degli
          // step): il nome dell'addon e' l'etichetta della sua riga nel
          // pannello, seguita dalla variante scelta.
          // Offerta: l'importo offerto in testa, porta allo step dell'offerta.
          if (this.dataset.mode === "bid" && this.bidState && this.bidState.value) {
            riga(
              t.tOffer || "Offer",
              Math.round(this.bidState.value * 100),
              "bid-offer",
              "",
              -1,
            );
          }
          scelta("service-pack", "kit");
          scelta("box", "box");
          var addons = this._addonChoices || {};
          var etichettaRiga = function (k) {
            var lab = self.querySelector('[data-cfg-addon-row="' + k + '"] .bike-configurator__summary-label');
            var n = lab && lab.firstChild;
            return n && n.nodeType === 3 ? n.textContent.trim() : "";
          };
          ADDON_KEYS.forEach(function (k) {
            var a = addons[k];
            if (!a) return;
            var nome = etichettaRiga(k) || a.title;
            if (a.title && !a.plain && a.title !== nome) nome += " · " + a.title;
            riga(nome, a.cents, k === "film" ? "box" : "custom", "upg", k === "film" ? 6 : null);
          });

          // Prodotti degli step (pedali, accessori, migliora): lo step si
          // ricava dalla card; un prodotto senza card resta negli accessori.
          var pieni = {};
          var extras = this._sessionExtras || {};
          Object.keys(extras).forEach(function (id) {
            var card = self.querySelector('quick-view[data-upsell-variant-id="' + id + '"]');
            var panel = card && card.closest("[data-cfg-step-panel]");
            var step = panel ? panel.dataset.cfgStepPanel : "accessories";
            pieni[step] = true;
            riga(extras[id].title, extras[id].cents || 0, step, "acc");
          });
          var film = addons.film;
          righe.sort(function (x, y) {
            return x.rango - y.rango || x.n - y.n;
          });

          var h = '<div class="cd-receipt__card"><p class="cd-receipt__title">' + esc(t.tTitle) + "</p>";
          righe.forEach(function (r) {
            h +=
              '<button type="button" class="cd-receipt__line" data-cfg-summary-jump="' + r.step + '">' +
              '<span class="cd-receipt__name">' + esc(r.testo) + "</span>" +
              '<span class="cd-receipt__price">' + esc(prezzo(r.cents)) + "</span></button>";
          });
          // Totale di accessori e upgrade, kit e box esclusi (Davide,
          // 23/10/2026): l'etichetta dice cosa somma, e senza nessuno dei
          // due la riga non c'e'.
          var totale = 0;
          var conUpg = false;
          var conAcc = false;
          righe.forEach(function (r) {
            if (r.tipo === "upg") conUpg = true;
            else if (r.tipo === "acc") conAcc = true;
            else return;
            totale += r.cents || 0;
          });
          if (conUpg || conAcc) {
            var etTot = conUpg && conAcc ? t.tTotalBoth : conUpg ? t.tTotalUpg : t.tTotalAcc;
            h +=
              '<div class="cd-receipt__line cd-receipt__line--total">' +
              '<span class="cd-receipt__name">' + esc(etTot || "Total") + "</span>" +
              '<span class="cd-receipt__price">' + esc(prezzo(totale)) + "</span></div>";
          }
          // Rider Protection non sta nello scontrino (Davide, 23/10/2026):
          // non e' una scelta del cliente; il prezzo resta nel pannello del
          // totale, in fondo.
          h += '<p class="cd-receipt__hint">' + esc(t.tHint) + "</p></div>";

          // "Aggiungi ancora": step facoltativi presenti per questa bici e
          // ancora vuoti, nell'ordine del wizard.
          var passi = this._steps();
          var chip = [];
          if (passi.indexOf("custom") !== -1) {
            var custom = CUSTOM_ADDON_KEYS.some(function (k) { return !!addons[k]; });
            if (!custom) chip.push("custom");
          }
          ["pedals", "accessories", "upgrade"].forEach(function (s) {
            // "Continua senza pedali" e' una scelta: niente "+ Pedali".
            if (s === "pedals" && self._noPedals) return;
            if (passi.indexOf(s) !== -1 && !pieni[s]) chip.push(s);
          });
          var brevi = { custom: t.sCustom, pedals: t.sPedals, accessories: t.sAccessories, upgrade: t.sUpgrade };
          var nomeStep = function (s) {
            if (brevi[s]) return brevi[s];
            var p = self.querySelector('[data-cfg-step-panel="' + s + '"]');
            return (p && p.dataset.cfgStepName) || s;
          };
          var chips = chip.map(function (s) {
            return '<button type="button" class="cd-receipt__chip" data-cfg-summary-jump="' + s + '">+ ' + esc(nomeStep(s)) + "</button>";
          });
          if (!film && this.querySelector('[data-cfg-addon="film"]')) {
            chips.push('<button type="button" class="cd-receipt__chip" data-cfg-summary-jump="box">+ ' + esc(t.tFilm) + "</button>");
          }
          if (chips.length) {
            h += '<p class="cd-receipt__more">' + esc(t.tMore) + '</p><div class="cd-receipt__chips">' + chips.join("") + "</div>";
          }
          box.innerHTML = h;
        }

        // PDP variant changed (size/color): treat it as a new bike. Sync the
        // variant id, price, protection and Size/Color chips, then reset any
        // prior selection so the next open is clean.
        onVariantChange(e) {
          var v = e && e.detail && e.detail.variant;
          if (!v) return;
          if (v.id != null) this.dataset.bikeVariantId = String(v.id);
          // Dati bid-mode: regime fiscale e matrice soglie dipendono da
          // compare_at/taxable/venditore della variante corrente.
          if (v.compare_at_price != null)
            this.dataset.comparePrice = String(v.compare_at_price || 0);
          // L'opzione "Dealer" porta il nome del venditore OPPURE il suo id
          // con zero-padding a 3 cifre: il nostro magazzino compare sia come
          // "Bikeroom" sia come "002" (misurato su produzione, ~meta e meta).
          // Stesso controllo nel Liquid che rende data-is-bkrm.
          if (v.option1 != null)
            this.dataset.isBkrm = String(v.option1 === "Bikeroom" || v.option1 === "002");
          if (v.taxable != null) this.dataset.taxable = String(!!v.taxable);
          if (typeof v.price === "number") {
            this.dataset.bikePrice = String(v.price);
            // Recompute protection from the variant price: floor(price*3/100) +
            // fixed fee (constant), mirroring the Liquid formula. Disabled when
            // the product is tagged no_protection.
            if (this.dataset.protectionEnabled === "true") {
              var fixed = parseInt(this.dataset.protectionFixed || "0", 10);
              this.dataset.protectionPrice = String(
                Math.floor((v.price * 3) / 100) + fixed,
              );
            } else {
              this.dataset.protectionPrice = "0";
            }
          }
          // querySelectorAll (non querySelector): il chip Color/Size è
          // duplicato in due punti del DOM (header desktop + colonna
          // immagine mobile, vedi product-configurator.liquid) — un solo
          // querySelector aggiornerebbe solo la prima occorrenza, lasciando
          // l'altra stale al cambio variante.
          if (v.option2 != null && v.option2 !== "") {
            this.querySelectorAll("[data-cfg-size-value]").forEach(function (el) {
              el.textContent = v.option2;
            });
          }
          if (v.option3 != null && v.option3 !== "") {
            this.querySelectorAll("[data-cfg-color-value]").forEach(function (el) {
              el.textContent = v.option3;
            });
          }
          // Panel image: like updateMedia() on the PDP gallery, only swap when
          // the new variant has its own featured_image — otherwise leave the
          // server-rendered image (already resolved from the initial variant)
          // as-is rather than resetting it to the product default.
          if (v.featured_image && v.featured_image.src) {
            var src520 = sizedImageUrl(v.featured_image.src, 520);
            var src1040 = sizedImageUrl(v.featured_image.src, 1040);
            var srcset = src520 + " 520w, " + src1040 + " 1040w";
            this.querySelectorAll(".cart-drawer-product-image img").forEach(function (img) {
              img.src = src520;
              img.setAttribute("srcset", srcset);
              img.setAttribute("data-srcset", srcset);
              if (v.featured_image.alt) img.alt = v.featured_image.alt;
            });
          }
          // Different bike → drop the previous selection and session upsells.
          // _bidUpsells incluso: lasciarlo popolato mentre _sessionExtras si
          // svuota disallinea la guardia del toggle bid dallo stato visivo
          // delle card (primo click che "non fa nulla" + re-add fantasma).
          // Gli upsell aggiunti in questa sessione portano il parent della
          // variante PRECEDENTE: toglierli dal carrello invece di lasciarli
          // agganciati a una bici che non ci arriverà (removeSessionExtras
          // svuota lo stato in modo sincrono; no-op in edit-mode).
          this.removeSessionExtras();
          this.resetSelection();
          this._sessionExtras = {};
          this._bidUpsells = {};
          this._extrasCents = 0;
          this.renderExtras();
        }

        // Record an upsell added via <quick-view> while this configurator is
        // open, so it shows up in the recap and bumps the subtotal. Only reacts
        // when open: items added by other flows (or our own atomic bike+kit+box
        // add on Proceed, which uses raw fetch and navigates away) don't apply.
        onCartAdded(e) {
          if (!this.classList.contains("is-open")) return;
          var line = e && e.detail && e.detail.product;
          if (!line) return;
          // Never record our own configurator-managed kit/box.
          if (line.properties && line.properties.__bkrm_drawer_select) return;
          var variantId = String(line.id || line.variant_id || "");
          if (!variantId) return;
          var qty = line.quantity || 1;
          var lineCents =
            typeof line.final_line_price === "number"
              ? line.final_line_price
              : typeof line.line_price === "number"
                ? line.line_price
                : (line.price || 0) * qty;
          this._sessionExtras[variantId] = {
            title: line.product_title || line.title || "",
            cents: lineCents,
            qty: qty,
            // Line key for precise removal if the user abandons the config
            // without completing the bike (see removeSessionExtras).
            key: line.key || null,
          };
          this.renderExtras();
          // Keep the cart icon frozen at the open-time count: this accessory is
          // provisional until Proceed. The theme already re-rendered the badge
          // (renderContents) BEFORE firing cart:item-added, so this runs last and
          // wins without a race.
          if (this._baseItemCount != null) {
            this.updateCartCount(this._baseItemCount);
          }
        }

        // Render the per-upsell rows into the recap and recompute _extrasCents,
        // then refresh the totals. Clearing the container here also wipes stale
        // rows when the panel is reopened for a different bike.
        renderExtras() {
          var container = this.querySelector(
            ".bike-configurator__summary-extras",
          );
          var extras = this._sessionExtras || {};
          var keys = Object.keys(extras);
          // Stesso ordine dello scontrino: per step (pedali, accessori,
          // migliora), poi nell'ordine di scelta.
          var self = this;
          var rangoStep = { pedals: 0, accessories: 1, upgrade: 2 };
          var rangoDi = function (id) {
            var c = self.querySelector('quick-view[data-upsell-variant-id="' + id + '"]');
            var pn = c && c.closest("[data-cfg-step-panel]");
            var s = pn ? pn.dataset.cfgStepPanel : "accessories";
            return s in rangoStep ? rangoStep[s] : 1;
          };
          keys = keys
            .map(function (id, n) { return { id: id, r: rangoDi(id), n: n }; })
            .sort(function (x, y) { return x.r - y.r || x.n - y.n; })
            .map(function (x) { return x.id; });
          var total = 0;
          var html = "";
          var removeLabel = this.dataset.tRemove || "Remove";
          // Stessa icona X del Liquid (data-cfg-x-icon): una copia sola.
          var closeIcon = this.dataset.cfgXIcon || "";
          for (var i = 0; i < keys.length; i++) {
            var x = extras[keys[i]];
            total += x.cents;
            // Niente sottoriga con lo step di provenienza ("Pedali",
            // "Accessori"...): nel pannello restano solo le specifiche, che
            // per gli extra sono gia' nel titolo (taglia, misura). Feedback
            // di Davide, 23/10/2026.
            var sub = "";
            html +=
              '<div class="bike-configurator__summary-row bike-configurator__summary-extra">' +
              '<span class="bike-configurator__summary-label">' +
              escapeHtml(x.title) +
              (sub
                ? '<span class="bike-configurator__summary-product-sub">' +
                  escapeHtml(sub) +
                  "</span>"
                : "") +
              "</span>" +
              '<span class="bike-configurator__summary-price">' +
              formatCents(x.cents) +
              "</span>" +
              '<button type="button" class="bike-configurator__summary-remove" data-cfg-summary-remove="extra" data-variant-id="' +
              escapeHtml(keys[i]) +
              '" aria-label="' +
              escapeHtml(removeLabel + " " + x.title) +
              '">' +
              closeIcon +
              "</button>" +
              "</div>";
          }
          if (container) container.innerHTML = html;
          this._extrasCents = total;
          // Riflette lo stato "selezionata" (pallino) sulle card upsell: una
          // card è selezionata quando la sua variante è tra gli extra di
          // sessione. Punto unico per entrambi i flussi — bid (toggle nel
          // payload) e "Complete your bike" (add al carrello via onCartAdded) —
          // perché tutti finiscono qui via _sessionExtras.
          var upsellCards = this.querySelectorAll(
            "quick-view[data-upsell-variant-id]",
          );
          for (var c = 0; c < upsellCards.length; c++) {
            var cardEl = upsellCards[c];
            var isAdded = Object.prototype.hasOwnProperty.call(
              extras,
              cardEl.dataset.upsellVariantId,
            );
            cardEl.classList.toggle("is-upsell-added", isAdded);
            // A card aggiunta la taglia non si cambia: si toglie e si
            // rimette, altrimenti la select direbbe L e il carrello M.
            var cardPicker = cardEl.querySelector("[data-cfg-upsell-variant]");
            if (cardPicker) cardPicker.disabled = isAdded;
          }
          this.updateSummary();
        }

        // Show the exit-confirmation alert (instead of closing immediately).
        showExitAlert() {
          var alertEl = this.querySelector(
            ".bike-configurator__alert:not([data-cfg-pedals-confirm])",
          );
          if (!alertEl) {
            // Difensivo: se il markup dell'alert manca, chiudi come prima.
            this.close();
            return;
          }
          alertEl.removeAttribute("hidden");
          var cont = alertEl.querySelector(".bike-configurator__alert-continue");
          if (cont) cont.focus();
        }

        hideExitAlert() {
          var alertEl = this.querySelector(
            ".bike-configurator__alert:not([data-cfg-pedals-confirm])",
          );
          if (alertEl) alertEl.setAttribute("hidden", "");
        }

        close() {
          // Always drop the exit alert so a later reopen never starts with it open.
          this.hideExitAlert();
          var pedalsConfirm = this._pedalsConfirmEl();
          if (pedalsConfirm) pedalsConfirm.setAttribute("hidden", "");
          // Abandoning the config (X / backdrop / no "Proceed"): drop any upsell
          // added during this session from the live cart, so an accessory never
          // lingers without its bike. Proceed() navigates away instead of calling
          // close(), so completed configs keep their accessories.
          this.removeSessionExtras();
          this.setAttribute("hidden", "");
          this.classList.remove("is-open");
          document.documentElement.classList.remove("bike-configurator-open");
          document.body.classList.remove("bike-configurator-open");
        }

        // Remove the session's upsell lines from the cart (orphan cleanup on
        // abandon). No-op in edit mode (cart page: the bike already exists, so
        // accessories belong there) and when nothing was added.
        async removeSessionExtras() {
          if (this.dataset.editMode === "true") return;
          var extras = this._sessionExtras || {};
          // Solo gli upsell aggiunti in questa sessione sono provvisori: gli
          // item già presenti all'apertura (sessionAdded:false) NON vanno
          // rimossi all'abbandono. Le voci senza flag (legacy onCartAdded)
          // restano trattate come rimovibili.
          var lineKeys = Object.keys(extras)
            .filter(function (k) {
              return extras[k].sessionAdded !== false;
            })
            .map(function (k) {
              return extras[k].key;
            })
            .filter(Boolean);
          // Clear local state first so a second close() doesn't double-remove.
          this._sessionExtras = {};
          this._extrasCents = 0;
          this.renderExtras();
          if (!lineKeys.length) return;
          var root =
            (window.Shopify &&
              window.Shopify.routes &&
              window.Shopify.routes.root) ||
            "/";
          var lastCart = null;
          for (var i = 0; i < lineKeys.length; i++) {
            try {
              var res = await fetch(root + "cart/change.js", {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Accept: "application/json",
                },
                body: JSON.stringify({ id: lineKeys[i], quantity: 0 }),
              });
              if (res.ok) lastCart = await res.json();
            } catch (err) {
              /* best-effort: leave the line if the network call fails */
            }
          }
          if (lastCart && typeof lastCart.item_count === "number") {
            this.updateCartCount(lastCart.item_count);
          }
          document.dispatchEvent(
            new CustomEvent("cart:refresh", { bubbles: true }),
          );
        }

        updateCartCount(count) {
          document.querySelectorAll(".thb-item-count").forEach(function (el) {
            el.textContent = count;
          });
        }

        // Read the current cart-icon badge count (integer), or null if absent.
        _readCartCount() {
          var el = document.querySelector(".thb-item-count");
          if (!el) return null;
          var n = parseInt((el.textContent || "").trim(), 10);
          return isNaN(n) ? null : n;
        }
      },
    );
  }

  // Cart page: each bike line has an "Edit" link (<a data-cart-open-drawer
  // data-bike-variant="…">). Open the matching configurator overlay (rendered
  // per-bike on the cart page in edit mode). Delegated so it works regardless
  // of how the cart section is re-rendered.
  document.addEventListener("click", function (e) {
    var opener = e.target.closest("[data-cart-open-drawer]");
    if (!opener) return;
    e.preventDefault();
    var vid = opener.getAttribute("data-bike-variant");
    var cfg = vid
      ? document.querySelector(
          'bike-configurator[data-bike-variant-id="' + vid + '"]',
        )
      : document.querySelector("bike-configurator");
    if (cfg && typeof cfg.open === "function") cfg.open();
  });
})();
